import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Info, ChevronDown, Plus, ZoomOut, ActivityIcon, Cpu } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Tabs } from '../components/ui/Tabs';
import { useWeatherForecast, getVariableValue } from '../hooks/useWeatherData';
import { useJuaForecast, hasJuaApiKey, MODEL_ID_MAP } from '../hooks/useJuaForecast';
import { useJuaObservations } from '../hooks/useJuaObservations';
import { ModelRunSelector, MODELS as MODEL_CONFIGS, type ModelOverride, type ModelRun, getDefaultRuns, getDefaultHiddenModels } from '../components/workspace/ModelRunSelector';
import { LocationSelector } from '../components/workspace/LocationSelector';
import { VariableSelector } from '../components/workspace/VariableSelector';
import { TimeframeSelector } from '../components/workspace/TimeframeSelector';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceArea, Brush } from 'recharts';
import { useTheme } from '../hooks/useTheme';
import { useUserProfile } from '../hooks/useUserProfile';
import { MarketZoneMap, type MarketZoneSelection } from '../components/workspace/MarketZoneMap';
// Variable configuration for chart data generation
interface VariableConfig {
  unit: string;
  base: [number, number]; // [min, max] for base value
  diurnal: number; // amplitude of diurnal cycle
  noise: number; // random noise amplitude
  decimals: number;
  label: string;
}
const VARIABLE_CONFIGS: Record<string, VariableConfig> = {
  'Wind Speed 10m': {
    unit: 'm/s',
    base: [3, 8],
    diurnal: 1.5,
    noise: 1.2,
    decimals: 1,
    label: 'Wind Speed 10m'
  },
  'Wind Speed 100m': {
    unit: 'm/s',
    base: [6, 12],
    diurnal: 2.0,
    noise: 1.5,
    decimals: 1,
    label: 'Wind Speed 100m'
  },
  'Wind Dir 10m': {
    unit: '°',
    base: [180, 270],
    diurnal: 20,
    noise: 30,
    decimals: 0,
    label: 'Wind Direction 10m'
  },
  'Wind Dir 100m': {
    unit: '°',
    base: [180, 270],
    diurnal: 15,
    noise: 25,
    decimals: 0,
    label: 'Wind Direction 100m'
  },
  'Temp 2m': {
    unit: '°C',
    base: [8, 18],
    diurnal: 4,
    noise: 1.5,
    decimals: 1,
    label: 'Temperature 2m'
  },
  'Dew Point 2m': {
    unit: '°C',
    base: [4, 12],
    diurnal: 2,
    noise: 1.0,
    decimals: 1,
    label: 'Dew Point 2m'
  },
  'Precipitation 1h': {
    unit: 'mm',
    base: [0, 2],
    diurnal: 0.5,
    noise: 1.0,
    decimals: 1,
    label: 'Precipitation 1h'
  },
  'SSRD 1h': {
    unit: 'W/m²',
    base: [0, 200],
    diurnal: 300,
    noise: 50,
    decimals: 0,
    label: 'Solar Radiation 1h'
  },
  'MSL Pressure': {
    unit: 'hPa',
    base: [1010, 1020],
    diurnal: 1.5,
    noise: 1.0,
    decimals: 1,
    label: 'MSL Pressure'
  },
  'Cloud Cover High': {
    unit: '%',
    base: [20, 60],
    diurnal: 15,
    noise: 10,
    decimals: 0,
    label: 'Cloud Cover (High)'
  },
  'Cloud Cover Medium': {
    unit: '%',
    base: [25, 55],
    diurnal: 12,
    noise: 10,
    decimals: 0,
    label: 'Cloud Cover (Medium)'
  },
  'Cloud Cover Low': {
    unit: '%',
    base: [15, 50],
    diurnal: 10,
    noise: 10,
    decimals: 0,
    label: 'Cloud Cover (Low)'
  },
  'Day-ahead Prices': {
    unit: '€/MWh',
    base: [40, 80],
    diurnal: 20,
    noise: 10,
    decimals: 2,
    label: 'Day-ahead Prices'
  },
  'Imbalance Prices': {
    unit: '€/MWh',
    base: [30, 90],
    diurnal: 25,
    noise: 15,
    decimals: 2,
    label: 'Imbalance Prices'
  },
  'Generation (Actual)': {
    unit: 'GW',
    base: [50, 70],
    diurnal: 10,
    noise: 3,
    decimals: 1,
    label: 'Generation (Actual)'
  },
  'Generation Forecast (Day-ahead)': {
    unit: 'GW',
    base: [50, 70],
    diurnal: 10,
    noise: 4,
    decimals: 1,
    label: 'Generation Forecast'
  },
  'Wind & Solar Forecast (Day-ahead)': {
    unit: 'GW',
    base: [10, 30],
    diurnal: 8,
    noise: 4,
    decimals: 1,
    label: 'Wind & Solar (DA)'
  },
  'Wind & Solar Forecast (Intraday)': {
    unit: 'GW',
    base: [10, 30],
    diurnal: 8,
    noise: 5,
    decimals: 1,
    label: 'Wind & Solar (ID)'
  },
  'Load (Actual)': {
    unit: 'GW',
    base: [50, 70],
    diurnal: 15,
    noise: 3,
    decimals: 1,
    label: 'Load (Actual)'
  },
  'Load Forecast (Day-ahead)': {
    unit: 'GW',
    base: [50, 70],
    diurnal: 15,
    noise: 4,
    decimals: 1,
    label: 'Load Forecast'
  },
  'Available Hydro Capacity': {
    unit: 'GW',
    base: [8, 15],
    diurnal: 2,
    noise: 1,
    decimals: 1,
    label: 'Hydro Capacity'
  },
  'Cross-border Flows': {
    unit: 'GW',
    base: [-5, 5],
    diurnal: 3,
    noise: 2,
    decimals: 1,
    label: 'Cross-border Flows'
  }
};
const DEFAULT_VARIABLE_CONFIG: VariableConfig = {
  unit: '€/MWh',
  base: [4, 6],
  diurnal: 1.5,
  noise: 1.5,
  decimals: 2,
  label: 'Value'
};
// Seeded random for stable per-model data
function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = s * 16807 % 2147483647;
    return (s - 1) / 2147483646;
  };
}
// Generate 24h forecast data for a given model run (stable per modelId+timestamp+variable)
// Map timeframe IDs to total hours
const TIMEFRAME_HOURS: Record<string, number> = {
  id: 24,
  da: 48,
  wk: 168,
  mn: 720,
  qr: 2160
};
// Custom crosshair cursor for charts — renders both vertical and horizontal lines
function CrosshairCursor({
  points,
  width,
  height,
  top,
  left,
  stroke,
  strokeDasharray
}: any) {
  if (!points || !points.length) return null;
  const {
    x,
    y
  } = points[0];
  return <g data-id="element-3080">
      {/* Vertical line */}
      <line x1={x} y1={top || 0} x2={x} y2={(top || 0) + (height || 0)} stroke={stroke} strokeWidth={1.5} strokeDasharray={strokeDasharray} strokeOpacity={1} data-id="element-3081" />
      {/* Horizontal line */}
      <line x1={left || 0} y1={y} x2={(left || 0) + (width || 0)} y2={y} stroke={stroke} strokeWidth={0.7} strokeDasharray={strokeDasharray} strokeOpacity={0.6} data-id="element-3082" />
    </g>;
}
function generateRunData(modelId: string, timestamp: number, variable?: string, totalHours: number = 24, location: string = 'Germany'): number[] {
  const config = variable && VARIABLE_CONFIGS[variable] || DEFAULT_VARIABLE_CONFIG;
  const varSeed = variable ? variable.split('').reduce((a, c) => a + c.charCodeAt(0), 0) : 0;
  const locSeed = location.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const seed = modelId.split('').reduce((a, c) => a + c.charCodeAt(0), 0) * 1000 + timestamp % 100000 + varSeed + locSeed * 7;
  const rng = seededRandom(seed);
  const base = config.base[0] + rng() * (config.base[1] - config.base[0]);
  // Per-model persistent characteristics
  const modelBias = (rng() - 0.5) * config.diurnal * 0.8;
  const phaseShift = (rng() - 0.5) * 4; // ±2h shift
  const ampScale = 0.7 + rng() * 0.6;
  // Pre-generate smooth wandering noise
  const noise: number[] = [];
  let nv = (rng() - 0.5) * config.noise * 0.5;
  for (let i = 0; i < totalHours; i++) {
    nv += (rng() - 0.5) * config.noise * 0.15;
    nv *= 0.96;
    noise.push(nv);
  }
  return Array.from({
    length: totalHours
  }, (_, i) => {
    const shifted = i + phaseShift;
    const diurnal = Math.sin((shifted - 6) / 24 * Math.PI * 2) * config.diurnal * ampScale;
    // Add secondary harmonic for more complex shape
    const harmonic = Math.sin((shifted - 3) / 12 * Math.PI * 2) * config.diurnal * 0.3 * ampScale;
    const value = base + modelBias + diurnal + harmonic + noise[i];
    return +value.toFixed(config.decimals);
  });
}
export function EnergyMarketView() {
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  const {
    profile
  } = useUserProfile();
  // Standalone selectors for this page
  const [locationOverride, setLocationOverride] = useState(null);
  const [variableOverride, setVariableOverride] = useState<{
    variable: string;
  } | null>({
    variable: 'Wind Speed 10m'
  });
  const [timeframeOverride, setTimeframeOverride] = useState({
    timeframeId: 'wk',
    label: 'WK'
  } as any);
  const [weighting, setWeighting] = useState<string | null>(null);
  // Shared state for syncing both ModelRunSelector instances
  const [sharedSelectedRuns, setSharedSelectedRuns] = useState<ModelRun[]>(getDefaultRuns);
  const [sharedHiddenModels, setSharedHiddenModels] = useState<Set<string>>(getDefaultHiddenModels);
  const [weightingOpen, setWeightingOpen] = useState(false);
  const weightingRef = useRef<HTMLDivElement>(null);
  const weightingDropdownRef = useRef<HTMLDivElement>(null);
  const [chartMode, setChartMode] = useState('data');
  // Top chart always shows absolute data; delta is in bottom tabbed section
  const [modelOverride, setModelOverride] = useState<ModelOverride | null>(null);
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  const [baselineKey, setBaselineKey] = useState<string | null>(null);
  const [baselineDropdownOpen, setBaselineDropdownOpen] = useState(false);
  const baselineDropdownRef = useRef<HTMLDivElement>(null);
  const [legendGroupBy, setLegendGroupBy] = useState<'model' | 'run' | 'location'>('model');
  const [groupByDropdownOpen, setGroupByDropdownOpen] = useState(false);
  const groupByDropdownRef = useRef<HTMLDivElement>(null);
  const [addModelOpen, setAddModelOpen] = useState(false);
  const addModelRef = useRef<HTMLDivElement>(null);
  const [heatmapBaselineKey, setHeatmapBaselineKey] = useState<string | null>(null); // null = mean
  const [heatmapBaselineOpen, setHeatmapBaselineOpen] = useState(false);
  const heatmapBaselineRef = useRef<HTMLDivElement>(null);
  const [chartModelsOpen, setChartModelsOpen] = useState(false);
  const chartModelsRef = useRef<HTMLDivElement>(null);
  const chartModelsDropdownRef = useRef<HTMLDivElement>(null);
  const [bottomTab, setBottomTab] = useState<'delta' | 'table'>('delta');
  // Generate last 3 runs per model for the selection card
  const modelRunOptions = useMemo(() => {
    const now = new Date();
    return MODEL_CONFIGS.map(model => {
      const runs: {
        timestamp: number;
        label: string;
      }[] = [];
      for (let i = 0; i < 3; i++) {
        const d = new Date(now);
        if (model.id === 'ept-2-hrrr') {
          d.setUTCHours(d.getUTCHours() - i, 0, 0, 0);
        } else {
          const baseHour = Math.floor(d.getUTCHours() / 6) * 6;
          d.setUTCHours(baseHour - i * 6, 0, 0, 0);
        }
        runs.push({
          timestamp: d.getTime(),
          label: `${d.getUTCHours().toString().padStart(2, '0')}z`
        });
      }
      return {
        model,
        runs
      };
    });
  }, []);
  const isRunSelected = useCallback((modelId: string, timestamp: number) => {
    return modelOverride?.runs?.some(r => r.modelId === modelId && r.timestamp === timestamp) ?? false;
  }, [modelOverride]);
  const toggleRun = useCallback((modelId: string, modelName: string, timestamp: number) => {
    setModelOverride(prev => {
      const existingRuns = prev?.runs || [];
      const exists = existingRuns.some(r => r.modelId === modelId && r.timestamp === timestamp);
      let newRuns;
      if (exists) {
        newRuns = existingRuns.filter(r => !(r.modelId === modelId && r.timestamp === timestamp));
      } else {
        newRuns = [...existingRuns, {
          modelId,
          modelName,
          timestamp
        }];
      }
      if (newRuns.length === 0) return null;
      return {
        modelNames: [...new Set(newRuns.map(r => r.modelName))],
        runs: newRuns
      };
    });
  }, []);
  // Derive active variable and its config
  const activeVariable = variableOverride?.variable || 'Wind Speed 10m';
  const varConfig = VARIABLE_CONFIGS[activeVariable] || DEFAULT_VARIABLE_CONFIG;
  // Weighting options based on variable category
  const WEIGHTING_OPTIONS: Record<string, {
    options: string[];
    default: string;
  }> = {
    wind: {
      options: ['Wind capacity', 'Equal'],
      default: 'Wind capacity'
    },
    solar: {
      options: ['Solar capacity', 'Equal'],
      default: 'Solar capacity'
    },
    temp: {
      options: ['Population', 'Equal'],
      default: 'Population'
    },
    other: {
      options: ['Equal'],
      default: 'Equal'
    }
  };
  const getWeightingCategory = (v: string) => {
    const lower = v.toLowerCase();
    if (lower.includes('wind')) return 'wind';
    if (lower.includes('solar') || lower.includes('ssrd')) return 'solar';
    if (lower.includes('temp') || lower.includes('dew')) return 'temp';
    return 'other';
  };
  const weightingCategory = getWeightingCategory(activeVariable);
  const weightingConfig = WEIGHTING_OPTIONS[weightingCategory];
  const activeWeighting = weighting && weightingConfig.options.includes(weighting) ? weighting : weightingConfig.default;
  // Close weighting dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (weightingRef.current && !weightingRef.current.contains(target) && (!weightingDropdownRef.current || !weightingDropdownRef.current.contains(target))) {
        setWeightingOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  // Derive active location from selector (use regionId for market zones, regionName for others)
  const activeLocation = (locationOverride as any)?.regionId || (locationOverride as any)?.regionName || 'Germany';
  // Fetch real weather data for the active location (fallback)
  const {
    data: forecastData,
    loading: weatherLoading
  } = useWeatherForecast(activeLocation);
  // Derive total hours from timeframe selector
  const totalHours = (timeframeOverride as any)?.timeframeId ? TIMEFRAME_HOURS[(timeframeOverride as any).timeframeId] || 168 : 168;
  // Fetch real multi-model data from Jua Query Engine API (when key is configured)
  const juaModelNames = useMemo(() => {
    if (!modelOverride?.runs) return [];
    const names = new Set<string>();
    modelOverride.runs.forEach(run => {
      const config = MODEL_CONFIGS.find(m => m.id === run.modelId);
      if (config?.name && MODEL_ID_MAP[config.name]) names.add(config.name);
    });
    return Array.from(names);
  }, [modelOverride]);
  const {
    byModel: juaData,
    hasRealData: hasJuaData,
    loading: juaLoading
  } = useJuaForecast(juaModelNames, activeLocation, activeVariable, totalHours);
  // Zoom pull state (must be before useJuaObservations which depends on showObservations)
  const [showObservations, setShowObservations] = useState(false);
  const [pullProgress, setPullProgress] = useState(0);
  // Fetch real observation/hindcast data from Jua API
  const {
    observations: juaObservations,
    modelHindcasts: juaHindcasts,
    hasRealData: hasJuaObsData
  } = useJuaObservations(activeLocation, activeVariable, juaModelNames, showObservations);
  // Generate appropriate X-axis ticks based on total hours
  const xTicks = useMemo(() => {
    if (totalHours <= 12) {
      return Array.from({
        length: totalHours
      }, (_, i) => i);
    } else if (totalHours <= 24) {
      return Array.from({
        length: 12
      }, (_, i) => i * 2);
    } else if (totalHours <= 48) {
      return Array.from({
        length: Math.ceil(totalHours / 4)
      }, (_, i) => i * 4);
    } else if (totalHours <= 168) {
      return Array.from({
        length: Math.ceil(totalHours / 12)
      }, (_, i) => i * 12);
    } else {
      return Array.from({
        length: Math.ceil(totalHours / 24)
      }, (_, i) => i * 24);
    }
  }, [totalHours]);
  const shortDow = (d: Date) => {
    const names = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    return names[d.getDay()];
  };
  const formatHourTick = useCallback((v: number) => {
    const now = new Date();
    const d = new Date(now.getTime() + Math.round(v) * 3600000);
    const hh = String(d.getHours()).padStart(2, '0');
    const day = d.getDate();
    const mon = d.toLocaleDateString('en-GB', {
      month: 'short'
    });
    if (totalHours <= 48) {
      return `${day} ${mon} ${hh}:00`;
    }
    const hourOffset = Math.round(v);
    if (hourOffset % 24 === 0) {
      return `${day} ${mon}`;
    }
    return `${day} ${mon} ${hh}:00`;
  }, [totalHours]);
  // Compute midnight markers and weekend zones from hour offsets
  const {
    midnightHours,
    weekendRanges
  } = useMemo(() => {
    const now = new Date();
    const midnights: {
      hour: number;
      label: string;
      dow: string;
    }[] = [];
    const weekends: {
      start: number;
      end: number;
    }[] = [];
    let inWeekend = false;
    let weekendStart = 0;
    for (let h = 0; h <= totalHours; h++) {
      const d = new Date(now.getTime() + h * 3600000);
      const hour = d.getHours();
      const day = d.getDay(); // 0=Sun, 6=Sat
      const isWeekend = day === 0 || day === 6;
      // Midnight marker with day name
      if (hour === 0 && h > 0) {
        const dow = shortDow(d);
        const date = d.getDate();
        const mon = d.toLocaleDateString('en-GB', {
          month: 'short'
        });
        midnights.push({
          hour: h,
          label: `${date} ${mon}`,
          dow
        });
      }
      // Weekend tracking
      if (isWeekend && !inWeekend) {
        weekendStart = h;
        inWeekend = true;
      } else if (!isWeekend && inWeekend) {
        weekends.push({
          start: weekendStart,
          end: h
        });
        inWeekend = false;
      }
    }
    if (inWeekend) {
      weekends.push({
        start: weekendStart,
        end: totalHours
      });
    }
    return {
      midnightHours: midnights,
      weekendRanges: weekends
    };
  }, [totalHours]);
  const toggleLine = useCallback((key: string) => {
    setHiddenKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);
  const toggleGroup = useCallback((keys: string[]) => {
    setHiddenKeys(prev => {
      const next = new Set(prev);
      const allHidden = keys.every(k => next.has(k));
      if (allHidden) {
        keys.forEach(k => next.delete(k));
      } else {
        keys.forEach(k => next.add(k));
      }
      return next;
    });
  }, []);
  // Build chart entries from selected model runs using real weather data
  const chartEntries = useMemo(() => {
    if (!modelOverride?.runs?.length) return [];
    // Group runs by model to compute age-based opacity
    const byModel: Record<string, typeof modelOverride.runs> = {};
    modelOverride.runs.forEach(run => {
      if (!byModel[run.modelId]) byModel[run.modelId] = [];
      byModel[run.modelId].push(run);
    });
    // Sort each model's runs by timestamp descending (newest first)
    Object.values(byModel).forEach(runs => runs.sort((a, b) => b.timestamp - a.timestamp));
    return modelOverride.runs.map(run => {
      const config = MODEL_CONFIGS.find(m => m.id === run.modelId);
      const runDate = new Date(run.timestamp);
      const runLabel = `${runDate.getUTCHours().toString().padStart(2, '0')}z`;
      const sameModelRuns = byModel[run.modelId];
      const ageIndex = sameModelRuns.indexOf(run); // 0 = newest
      const sameModelCount = sameModelRuns.length;
      // Opacity: newest = 1.0, each older step -0.25, min 0.25
      const ageOpacity = sameModelCount > 1 ? Math.max(0.25, 1.0 - ageIndex * 0.25) : 1.0;
      // Build data from Jua API, real forecast, or fallback to synthetic
      let data: number[];
      // Try Jua API data first (real multi-model forecasts)
      const modelName = config?.name || run.modelId;
      const juaModelData = hasJuaData ? juaData[modelName] : null;
      if (juaModelData && juaModelData.length > 0) {
        // Use real Jua API data — each entry has { hour, value }
        data = Array.from({
          length: totalHours
        }, (_, h) => {
          // Find the closest data point for this hour
          const exact = juaModelData.find(d => Math.round(d.hour) === h);
          if (exact) return +exact.value.toFixed(varConfig.decimals);
          // Interpolate between nearest points
          const before = juaModelData.filter(d => d.hour <= h).pop();
          const after = juaModelData.find(d => d.hour > h);
          if (before && after && after.hour !== before.hour) {
            const frac = (h - before.hour) / (after.hour - before.hour);
            return +(before.value + (after.value - before.value) * frac).toFixed(varConfig.decimals);
          }
          if (before) return +before.value.toFixed(varConfig.decimals);
          if (after) return +after.value.toFixed(varConfig.decimals);
          return 0;
        });
        // Apply slight run-age variation for older runs of the same model
        if (ageIndex > 0) {
          const entryKey = `${run.modelId}-${run.timestamp}`;
          const seed = entryKey.split('').reduce((a, c) => a + c.charCodeAt(0), 0) * 137;
          const rng = seededRandom(seed);
          const runBias = (rng() - 0.5) * 0.05 * ageIndex;
          data = data.map(v => +(v * (1 + runBias) + (rng() - 0.5) * Math.abs(v) * 0.02 * ageIndex).toFixed(varConfig.decimals));
        }
      } else if (forecastData.length > 0) {
        // Use real weather data with per-model/run variation
        const entryKey = `${run.modelId}-${run.timestamp}`;
        const seed = entryKey.split('').reduce((a, c) => a + c.charCodeAt(0), 0) * 137;
        const rng = seededRandom(seed);
        // Model-specific spread: EPT-2 slightly tighter than others
        const isEpt = run.modelId.startsWith('ept-2');
        const modelSpread = isEpt ? 0.75 : 1.0;
        // Inter-model variance: large persistent differences matching real forecasts
        // Real models show 20-40% bias, 6-12h phase shifts, 0.6-1.5x amplitude
        const modelBias = (rng() - 0.5) * 0.7 * modelSpread; // ±35% persistent offset
        const phaseShift = (rng() - 0.5) * 18 * modelSpread; // ±9h shift between models
        const ampScale = 0.6 + rng() * 0.8 * modelSpread; // 0.6x to 1.4x amplitude
        // Intra-model variance: older runs drift moderately from newest
        const runBias = ageIndex > 0 ? (rng() - 0.5) * 0.2 * ageIndex * modelSpread : 0;
        // Model revision bump: older runs get a large localized divergence
        // simulating a major forecast revision (e.g. new run captures a weather event)
        const hasRevision = ageIndex > 0 && rng() > 0.3; // 70% chance of a revision bump
        const revisionCenter = Math.floor(rng() * 0.6 + 0.2); // 20-80% into the timeline
        const revisionWidth = 0.08 + rng() * 0.12; // affects 8-20% of the timeline
        const revisionStrength = (0.4 + rng() * 0.6) * ageIndex * modelSpread; // stronger for older runs
        // Build hourly base signal from real forecast data (interpolated from 3h)
        const forecastHours = forecastData.length * 3;
        const baseSignal: number[] = [];
        for (let i = 0; i < forecastHours; i++) {
          const fpI = Math.min(Math.floor(i / 3), forecastData.length - 1);
          const fpNext = Math.min(fpI + 1, forecastData.length - 1);
          const frac = i % 3 / 3;
          const v1 = getVariableValue(forecastData[fpI], activeVariable);
          const v2 = getVariableValue(forecastData[fpNext], activeVariable);
          baseSignal.push(v1 + (v2 - v1) * frac);
        }
        // Compute signal stats
        let baseMean = 0;
        for (const v of baseSignal) baseMean += Math.abs(v);
        baseMean /= baseSignal.length || 1;
        const biasOffset = (modelBias + runBias) * baseMean;
        // Unified approach: ALL hours read from baseSignal, wrapping for extended range
        // Same interpolation method everywhere — no separate branches
        const sigLen = baseSignal.length;
        // Helper: smoothly read from baseSignal at any fractional position (wraps around)
        const readSignal = (pos: number): number => {
          // Wrap position into signal range
          const wrapped = (pos % sigLen + sigLen) % sigLen;
          const idx = Math.floor(wrapped);
          const f = wrapped - idx;
          const v1 = baseSignal[idx];
          const v2 = baseSignal[(idx + 1) % sigLen];
          return v1 + (v2 - v1) * f;
        };
        data = Array.from({
          length: totalHours
        }, (_, hour) => {
          // Gradual model spread: tight early, growing with lead time
          const leadFactor = Math.min(1.0, Math.sqrt(hour / 120)) * modelSpread;
          // Read from signal with model-specific phase shift
          const shifted = hour + phaseShift * leadFactor;
          const v = readSignal(shifted);
          // Apply amplitude scaling around mean
          let scaled = baseMean + (v - baseMean) * ampScale;
          // Apply revision bump: localized large divergence for older runs
          if (hasRevision) {
            const t = hour / totalHours;
            const dist = Math.abs(t - revisionCenter) / revisionWidth;
            if (dist < 1) {
              // Smooth bell-shaped bump (cosine window)
              const bump = 0.5 * (1 + Math.cos(Math.PI * dist));
              scaled += bump * revisionStrength * baseMean;
            }
          }
          // Add bias that grows with lead time
          return +(scaled + biasOffset * leadFactor).toFixed(varConfig.decimals);
        });
      } else {
        // Fallback to synthetic data while loading
        data = generateRunData(run.modelId, run.timestamp, activeVariable, totalHours, activeLocation);
      }
      return {
        key: `${run.modelId}-${run.timestamp}`,
        modelId: run.modelId,
        modelName: run.modelName,
        color: config?.color || '#94a3b8',
        timestamp: run.timestamp,
        runLabel,
        ageIndex,
        ageOpacity,
        sameModelCount,
        location: activeLocation,
        data
      };
    });
  }, [modelOverride, activeVariable, totalHours, activeLocation, forecastData, varConfig.decimals, juaData, hasJuaData]);
  // Compute legend groups
  const legendGroups = useMemo(() => {
    const groups: {
      label: string;
      color: string;
      keys: string[];
      entries: typeof chartEntries;
    }[] = [];
    const map = new Map<string, typeof chartEntries>();
    chartEntries.forEach(entry => {
      let groupKey: string;
      if (legendGroupBy === 'model') {
        groupKey = entry.modelId;
      } else if (legendGroupBy === 'run') {
        groupKey = entry.runLabel;
      } else {
        groupKey = entry.location;
      }
      if (!map.has(groupKey)) map.set(groupKey, []);
      map.get(groupKey)!.push(entry);
    });
    map.forEach((entries, groupKey) => {
      // Sort entries by timestamp ascending: earliest left, latest right
      const sorted = [...entries].sort((a, b) => a.timestamp - b.timestamp);
      const label = legendGroupBy === 'model' ? sorted[0].modelName : legendGroupBy === 'run' ? `${groupKey} runs` : groupKey;
      groups.push({
        label,
        color: legendGroupBy === 'model' ? sorted[0].color : '#94a3b8',
        keys: sorted.map(e => e.key),
        entries: sorted
      });
    });
    return groups;
  }, [chartEntries, legendGroupBy]);
  // Build merged chart data: one row per hour, one column per entry
  const chartData = useMemo(() => {
    const OBS_HOURS = 48;
    const startHour = showObservations ? -OBS_HOURS : 0;
    const totalLength = totalHours - startHour;
    // ── Observation data: use real Jua API data when available, fall back to mock ──
    const useRealObs = hasJuaObsData && Object.keys(juaObservations).length > 0;
    // Stable pseudo-random generator (fallback only)
    const makeRng = (s: number) => {
      let state = s;
      return () => {
        const x = Math.sin(state++) * 10000;
        return x - Math.floor(x);
      };
    };
    // Observations array: index 0 = h=-48, index OBS_HOURS = h=0
    const observations: number[] = new Array(OBS_HOURS + 1);
    if (useRealObs) {
      // Real Jua API observations (averaged across models)
      for (let i = 0; i <= OBS_HOURS; i++) {
        const hourOffset = i - OBS_HOURS; // -48 to 0
        observations[i] = juaObservations[hourOffset] ?? NaN;
      }
      // Fill any gaps with linear interpolation
      for (let i = 1; i < OBS_HOURS; i++) {
        if (isNaN(observations[i])) {
          // Find prev and next valid values
          let prev = i - 1;
          while (prev >= 0 && isNaN(observations[prev])) prev--;
          let next = i + 1;
          while (next <= OBS_HOURS && isNaN(observations[next])) next++;
          if (prev >= 0 && next <= OBS_HOURS) {
            const t = (i - prev) / (next - prev);
            observations[i] = +(observations[prev] + t * (observations[next] - observations[prev])).toFixed(2);
          }
        }
      }
    } else {
      // Fallback: mock observations that connect to the average forecast at h=0
      const h0Avg = chartEntries.length > 0 ? chartEntries.reduce((sum, e) => sum + e.data[0], 0) / chartEntries.length : 0;
      observations[OBS_HOURS] = Math.max(0, h0Avg);
      let currentVal = h0Avg;
      const obsRng = makeRng(12345);
      for (let i = OBS_HOURS - 1; i >= 0; i--) {
        currentVal += (obsRng() - 0.5) * 2.5;
        observations[i] = Math.max(0, +currentVal.toFixed(1));
      }
    }
    // Per-model hindcast data
    const modelHindcasts: Record<string, number[]> = {};
    chartEntries.forEach(entry => {
      const hindcast: number[] = new Array(OBS_HOURS + 1);
      // Find the model display name for this entry
      const modelConfig = MODEL_CONFIGS.find(m => m.id === entry.modelId);
      const modelDisplayName = modelConfig?.name || '';
      const juaHindcast = juaHindcasts[modelDisplayName];
      if (useRealObs && juaHindcast && Object.keys(juaHindcast).length > 0) {
        // Real Jua API hindcast data for this model
        hindcast[OBS_HOURS] = entry.data[0]; // Connect at h=0
        for (let i = 0; i < OBS_HOURS; i++) {
          const hourOffset = i - OBS_HOURS; // -48 to -1
          hindcast[i] = juaHindcast[hourOffset] ?? NaN;
        }
        // Fill gaps with interpolation
        for (let i = 1; i < OBS_HOURS; i++) {
          if (isNaN(hindcast[i])) {
            let prev = i - 1;
            while (prev >= 0 && isNaN(hindcast[prev])) prev--;
            let next = i + 1;
            while (next <= OBS_HOURS && isNaN(hindcast[next])) next++;
            if (prev >= 0 && next <= OBS_HOURS) {
              const t = (i - prev) / (next - prev);
              hindcast[i] = +(hindcast[prev] + t * (hindcast[next] - hindcast[prev])).toFixed(2);
            }
          }
        }
      } else {
        // Fallback: mock hindcast
        const seed = entry.key.split('').reduce((a, c) => a + c.charCodeAt(0), 0) * 31;
        const rng = makeRng(seed);
        const bias = (rng() - 0.5) * 3;
        const noiseScale = 0.5 + rng() * 1.5;
        const phaseShift = Math.round((rng() - 0.5) * 4);
        hindcast[OBS_HOURS] = entry.data[0];
        for (let i = OBS_HOURS - 1; i >= 0; i--) {
          const obsIdx = Math.max(0, Math.min(OBS_HOURS, i + phaseShift));
          const obsVal = observations[obsIdx];
          const noise = (rng() - 0.5) * noiseScale;
          const blendToObs = i / OBS_HOURS;
          const modelVal = obsVal + bias * (1 - blendToObs * 0.5) + noise;
          hindcast[i] = Math.max(0, +modelVal.toFixed(1));
        }
      }
      modelHindcasts[entry.key] = hindcast;
    });
    return Array.from({
      length: totalLength
    }, (_, i) => {
      const h = startHour + i;
      const row: Record<string, number> = {
        hour: h
      };
      if (h >= 0) {
        // Forecast region: use existing model data
        chartEntries.forEach(entry => {
          row[entry.key] = entry.data[h];
        });
      }
      if (h < 0 && showObservations) {
        // Observations region: populate model hindcasts
        chartEntries.forEach(entry => {
          const hindcast = modelHindcasts[entry.key];
          if (hindcast) {
            row[entry.key] = hindcast[h + OBS_HOURS];
          }
        });
      }
      if (h === 0 && showObservations) {
        // At h=0, both observations and forecasts connect
        chartEntries.forEach(entry => {
          row[entry.key] = entry.data[0];
        });
      }
      if (h <= 0 && showObservations) {
        row['observations'] = observations[h + OBS_HOURS];
      }
      return row;
    });
  }, [chartEntries, totalHours, showObservations, hasJuaObsData, juaObservations, juaHindcasts]);
  // Resolve baseline entry: use selected or default to first
  const baselineEntry = useMemo(() => {
    if (chartEntries.length < 2) return null;
    return chartEntries.find(e => e.key === baselineKey) || chartEntries[0];
  }, [chartEntries, baselineKey]);
  // Non-baseline entries for delta comparison
  const deltaEntries = useMemo(() => {
    if (!baselineEntry) return [];
    return chartEntries.filter(e => e.key !== baselineEntry.key);
  }, [chartEntries, baselineEntry]);
  // Delta data: difference of each entry vs baseline
  const deltaData = useMemo(() => {
    if (!baselineEntry || deltaEntries.length === 0) return [];
    const startHour = showObservations ? -48 : 0;
    const totalLength = totalHours - startHour;
    return Array.from({
      length: totalLength
    }, (_, i) => {
      const h = startHour + i;
      const row: Record<string, number> = {
        hour: h
      };
      if (h >= 0) {
        // Forecast region: use model forecast data
        deltaEntries.forEach(entry => {
          row[`delta-${entry.key}`] = +(entry.data[h] - baselineEntry.data[h]).toFixed(2);
        });
      } else if (showObservations && chartData.length > 0) {
        // Observation region: use hindcast data from chartData
        const chartRow = chartData[h - startHour];
        if (chartRow) {
          const baseVal = chartRow[baselineEntry.key];
          if (baseVal !== undefined && !isNaN(baseVal)) {
            deltaEntries.forEach(entry => {
              const entryVal = chartRow[entry.key];
              if (entryVal !== undefined && !isNaN(entryVal)) {
                row[`delta-${entry.key}`] = +(entryVal - baseVal).toFixed(2);
              }
            });
          }
        }
      }
      return row;
    });
  }, [baselineEntry, deltaEntries, totalHours, showObservations, chartData]);
  // Simple Y padding for chart domain
  const yPadding = 0.5;
  // Scroll-to-zoom for the forecast chart
  // Unified zoom state shared between scroll-zoom and Brush
  const [brushRange, setBrushRange] = useState<{
    startIndex: number;
    endIndex: number;
  } | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const isZoomed = brushRange !== null;
  // Counter to force-remount the Brush component on reset, avoiding stale onChange
  const [brushKey, setBrushKey] = useState(0);
  const isResettingRef = useRef(false);
  const resetZoom = useCallback(() => {
    isResettingRef.current = true;
    setBrushRange(null);
    setBrushKey(k => k + 1);
    // Clear the guard after the Brush has fully re-rendered (3 frames to be safe)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isResettingRef.current = false;
        });
      });
    });
  }, []);
  const handleBrushChange = useCallback((range: any) => {
    if (isResettingRef.current) return;
    if (!range || range.startIndex === undefined || range.endIndex === undefined) return;
    const {
      startIndex,
      endIndex
    } = range;
    // If the brush covers the full range, treat as unzoomed
    if (startIndex === 0 && endIndex === chartData.length - 1) {
      setBrushRange(null);
    } else {
      setBrushRange({
        startIndex,
        endIndex
      });
    }
  }, [chartData.length]);
  // ── rAF-batched scroll zoom + pan ──
  // All wheel deltas accumulate in refs; a single rAF tick applies them.
  // This avoids multiple React re-renders per frame for buttery smoothness.
  const gestureAxisRef = useRef<'x' | 'y' | null>(null);
  const gestureTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gestureAccumX = useRef(0);
  const gestureAccumY = useRef(0);
  const pendingDeltaX = useRef(0);
  const pendingDeltaY = useRef(0);
  const pendingMouseX = useRef(0.5);
  const rafIdRef = useRef<number | null>(null);
  const panAccumRef = useRef(0);
  const zoomAccumRef = useRef(0);
  // Keep latest state in refs so the rAF tick reads fresh values
  const brushRangeRef = useRef(brushRange);
  brushRangeRef.current = brushRange;
  const chartDataLenRef = useRef(chartData.length);
  chartDataLenRef.current = chartData.length;
  const showObsRef = useRef(showObservations);
  showObsRef.current = showObservations;
  // The rAF tick — reads accumulated deltas, applies one state update
  const flushScrollFrame = useCallback(() => {
    rafIdRef.current = null;
    const total = chartDataLenRef.current;
    if (!total) return;
    const dx = pendingDeltaX.current;
    const dy = pendingDeltaY.current;
    const mouseXRatio = pendingMouseX.current;
    pendingDeltaX.current = 0;
    pendingDeltaY.current = 0;
    const currentBrush = brushRangeRef.current;
    const currentStart = currentBrush?.startIndex ?? 0;
    const currentEnd = currentBrush?.endIndex ?? total - 1;
    const currentRange = currentEnd - currentStart;
    // ── Horizontal → Pan ──
    if (gestureAxisRef.current === 'x') {
      if (currentBrush) {
        const panSensitivity = currentRange / 600;
        panAccumRef.current += dx * panSensitivity;
        const panSteps = Math.trunc(panAccumRef.current);
        if (panSteps !== 0) {
          panAccumRef.current -= panSteps;
          let newStart = currentStart + panSteps;
          let newEnd = currentEnd + panSteps;
          if (newStart < 0) {
            newEnd += -newStart;
            newStart = 0;
          }
          if (newEnd >= total) {
            newStart -= newEnd - (total - 1);
            newEnd = total - 1;
          }
          newStart = Math.max(0, newStart);
          newEnd = Math.min(total - 1, newEnd);
          setBrushRange({
            startIndex: newStart,
            endIndex: newEnd
          });
        }
      }
      return;
    }
    // ── Vertical → Zoom ──
    if (gestureAxisRef.current !== 'y') return;
    if (Math.abs(dy) < 0.1) return;
    const zoomSensitivity = 0.001;
    const normalizedDelta = dy * zoomSensitivity * currentRange;
    zoomAccumRef.current += normalizedDelta;
    const zoomSteps = Math.trunc(zoomAccumRef.current);
    if (zoomSteps === 0) return;
    zoomAccumRef.current -= zoomSteps;
    const delta = zoomSteps;
    const newRange = currentRange + delta;
    if (newRange < 5) return;
    if (newRange >= total - 1) {
      setBrushRange(null);
      return;
    }
    const leftDelta = Math.round(delta * mouseXRatio);
    const rightDelta = delta - leftDelta;
    let newStart = currentStart - leftDelta;
    let newEnd = currentEnd + rightDelta;
    if (newStart < 0) {
      newEnd += -newStart;
      newStart = 0;
    }
    if (newEnd >= total) {
      newStart -= newEnd - (total - 1);
      newEnd = total - 1;
    }
    newStart = Math.max(0, newStart);
    newEnd = Math.min(total - 1, newEnd);
    setBrushRange({
      startIndex: newStart,
      endIndex: newEnd
    });
  }, []);
  // Wheel event collector — only accumulates, never sets state directly
  const handleChartWheel = useCallback((e: React.WheelEvent) => {
    if (!chartDataLenRef.current) return;
    e.preventDefault();
    const LOCK_THRESHOLD = 10;
    const GESTURE_TIMEOUT = 180;
    gestureAccumX.current += Math.abs(e.deltaX);
    gestureAccumY.current += Math.abs(e.deltaY);
    // Reset gesture lock after a pause
    if (gestureTimeoutRef.current) clearTimeout(gestureTimeoutRef.current);
    gestureTimeoutRef.current = setTimeout(() => {
      gestureAxisRef.current = null;
      gestureAccumX.current = 0;
      gestureAccumY.current = 0;
      zoomAccumRef.current = 0;
      panAccumRef.current = 0;
    }, GESTURE_TIMEOUT);
    // Lock to an axis once threshold is reached
    if (!gestureAxisRef.current) {
      if (gestureAccumX.current > LOCK_THRESHOLD && gestureAccumX.current > gestureAccumY.current * 1.8) {
        gestureAxisRef.current = 'x';
      } else if (gestureAccumY.current > LOCK_THRESHOLD && gestureAccumY.current > gestureAccumX.current * 1.8) {
        gestureAxisRef.current = 'y';
      } else {
        return; // not enough data yet
      }
    }
    // Accumulate deltas
    pendingDeltaX.current += e.deltaX;
    pendingDeltaY.current += e.deltaY;
    // Track mouse position for anchor-point zoom
    const rect = chartContainerRef.current?.getBoundingClientRect() ?? deltaChartContainerRef.current?.getBoundingClientRect();
    if (rect) {
      pendingMouseX.current = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    }
    // Schedule a single rAF tick if not already pending
    if (rafIdRef.current === null) {
      rafIdRef.current = requestAnimationFrame(flushScrollFrame);
    }
  }, [flushScrollFrame]);
  // Ref for the delta chart container (shares zoom state with main chart)
  const deltaChartContainerRef = useRef<HTMLDivElement>(null);
  // Attach wheel handler as non-passive so preventDefault() works
  const wheelHandlerRef = useRef(handleChartWheel);
  wheelHandlerRef.current = handleChartWheel;
  useEffect(() => {
    const handler = (e: WheelEvent) => {
      wheelHandlerRef.current(e as unknown as React.WheelEvent);
    };
    const el = chartContainerRef.current;
    if (el) {
      el.addEventListener('wheel', handler, {
        passive: false
      });
    }
    const el2 = deltaChartContainerRef.current;
    if (el2) {
      el2.addEventListener('wheel', handler, {
        passive: false
      });
    }
    return () => {
      if (el) el.removeEventListener('wheel', handler);
      if (el2) el2.removeEventListener('wheel', handler);
    };
  }, []);
  // Click-to-drag panning
  const dragRef = useRef<{
    startX: number;
    startRange: {
      startIndex: number;
      endIndex: number;
    };
  } | null>(null);
  const handleChartMouseDown = useCallback((e: React.MouseEvent) => {
    if (!brushRange) return;
    // Find the container — use the closest parent with the ref, or fall back
    const container = e.currentTarget as HTMLDivElement || chartContainerRef.current;
    if (!container) return;
    // Only left click
    if (e.button !== 0) return;
    // Don't intercept clicks on the Brush area (bottom ~34px) — let Recharts handle those
    const rect = container.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const brushZoneTop = rect.height - 34;
    if (clickY >= brushZoneTop) return;
    // Prevent text selection during drag
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startRange: {
        ...brushRange
      }
    };
    container.style.cursor = 'grabbing';
    // Disable text selection globally during drag
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    const total = chartData.length;
    // Account for Y-axis width (~44px left margin)
    const chartWidth = rect.width - 48;
    const handleMouseMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      ev.preventDefault();
      const dx = ev.clientX - dragRef.current.startX;
      // Convert pixel delta to data index delta
      const rangeSize = dragRef.current.startRange.endIndex - dragRef.current.startRange.startIndex;
      const pointsPerPixel = rangeSize / chartWidth;
      const indexDelta = Math.round(-dx * pointsPerPixel);
      let newStart = dragRef.current.startRange.startIndex + indexDelta;
      let newEnd = dragRef.current.startRange.endIndex + indexDelta;
      // Clamp to bounds
      if (newStart < 0) {
        newEnd += -newStart;
        newStart = 0;
      }
      if (newEnd >= total) {
        newStart -= newEnd - (total - 1);
        newEnd = total - 1;
      }
      newStart = Math.max(0, newStart);
      newEnd = Math.min(total - 1, newEnd);
      setBrushRange({
        startIndex: newStart,
        endIndex: newEnd
      });
    };
    const handleMouseUp = () => {
      dragRef.current = null;
      container.style.cursor = isZoomed ? 'grab' : 'crosshair';
      // Re-enable text selection
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [brushRange, chartData.length, isZoomed]);
  // Derive zoomed delta data from the brush range
  const zoomedDeltaData = useMemo(() => {
    if (!brushRange || !deltaData.length) return deltaData;
    const startHour = chartData[brushRange.startIndex]?.hour ?? 0;
    const endHour = chartData[brushRange.endIndex]?.hour ?? totalHours;
    return deltaData.filter((d: any) => d.hour >= startHour && d.hour <= endHour);
  }, [brushRange, chartData, deltaData, totalHours]);
  // Models not yet in the table (for "Add Model" dropdown)
  const activeModelIds = useMemo(() => {
    return new Set(chartEntries.map(e => e.modelId));
  }, [chartEntries]);
  const availableModelsToAdd = useMemo(() => {
    return MODEL_CONFIGS.filter(m => !activeModelIds.has(m.id));
  }, [activeModelIds]);
  const handleAddModel = useCallback((modelConfig: (typeof MODEL_CONFIGS)[number]) => {
    // Generate a latest run timestamp for this model
    const now = new Date();
    const hour = Math.floor(now.getUTCHours() / 6) * 6;
    const runDate = new Date(now);
    runDate.setUTCHours(hour, 0, 0, 0);
    const newRun = {
      modelId: modelConfig.id,
      modelName: modelConfig.name,
      timestamp: runDate.getTime()
    };
    setModelOverride(prev => {
      const existingRuns = prev?.runs || [];
      return {
        runs: [...existingRuns, newRun]
      } as any;
    });
    setAddModelOpen(false);
  }, []);
  // Close add-model dropdown on outside click
  useEffect(() => {
    if (!addModelOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (addModelRef.current && !addModelRef.current.contains(e.target as Node)) {
        setAddModelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [addModelOpen]);
  // Close chart models dropdown on outside click
  useEffect(() => {
    if (!chartModelsOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (chartModelsRef.current && !chartModelsRef.current.contains(e.target as Node) && (!chartModelsDropdownRef.current || !chartModelsDropdownRef.current.contains(e.target as Node))) {
        setChartModelsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [chartModelsOpen]);
  // Close heatmap baseline dropdown on outside click
  useEffect(() => {
    if (!heatmapBaselineOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (heatmapBaselineRef.current && !heatmapBaselineRef.current.contains(e.target as Node)) {
        setHeatmapBaselineOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [heatmapBaselineOpen]);
  // Close baseline dropdown on outside click
  useEffect(() => {
    if (!baselineDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (baselineDropdownRef.current && !baselineDropdownRef.current.contains(e.target as Node)) {
        // Also check if click is inside the delta chart baseline dropdown
        const deltaDropdown = document.querySelector('[data-delta-baseline-dropdown]');
        if (deltaDropdown && deltaDropdown.contains(e.target as Node)) return;
        setBaselineDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [baselineDropdownOpen]);
  return <div className="jua-page-bg jua-energy-page h-[calc(100vh-64px)] w-full flex flex-col overflow-hidden" style={{
    backgroundColor: isLight ? '#f8fafc' : undefined
  }} data-id="element-3083">
      {/* Inputs Bar */}
      <div className="jua-inputs-bar flex items-center gap-2 sm:gap-3 px-3 md:px-6 py-2 flex-shrink-0 overflow-x-auto scrollbar-hide" style={{
      backgroundColor: isLight ? 'rgba(255,255,255,0.7)' : 'rgba(11,17,32,0.65)',
      borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(148,163,184,0.12)'}`,
      backdropFilter: 'blur(12px)'
    }} data-id="element-3084">
        <span className="hidden sm:inline-block text-[10px] font-semibold uppercase tracking-wider flex-shrink-0" style={{
        color: isLight ? '#64748b' : '#64748b'
      }} data-id="element-3085">
          Inputs
        </span>

        <div className="hidden sm:block h-4 w-px flex-shrink-0" style={{
        backgroundColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(148,163,184,0.15)'
      }} data-id="element-3086" />

        {/* Region - Hidden on small screens to prioritize model selector */}
        <div className="hidden md:block" data-id="element-3087">
          <LocationSelector mode="page" defaultRegionId="de" onOverrideChange={setLocationOverride} data-id="element-3088" />
        </div>

        <div className="hidden md:block h-4 w-px flex-shrink-0" style={{
        backgroundColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(148,163,184,0.15)'
      }} data-id="element-3089" />

        {/* Variable */}
        <VariableSelector mode="page" defaultVariable="Wind Speed 10m" onOverrideChange={setVariableOverride} data-id="element-3090" />

        <div className="hidden sm:block h-4 w-px flex-shrink-0" style={{
        backgroundColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(148,163,184,0.15)'
      }} data-id="element-3091" />

        {/* Weighting */}
        <div className="relative flex-shrink-0" ref={weightingRef} data-id="element-3092">
          <button onClick={() => setWeightingOpen(!weightingOpen)} className={`flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-xs font-medium transition-all border ${weightingOpen ? 'bg-[#EC4899]/10 border-[#EC4899]/30 text-[#EC4899]' : isLight ? 'bg-black/[0.03] border-black/[0.08] text-slate-700 hover:bg-black/[0.06] hover:text-slate-900' : 'bg-white/[0.04] border-white/[0.08] text-slate-300 hover:bg-white/[0.08] hover:text-white'}`} data-id="element-3093">
            <span className="font-bold" data-id="element-3094">{activeWeighting}</span>
            <span className={weightingOpen ? 'text-[#EC4899]/40' : ''} style={!weightingOpen ? {
            color: isLight ? '#94a3b8' : '#475569'
          } : undefined} data-id="element-3095">
              ·
            </span>
            <span className={weightingOpen ? 'text-[#EC4899]/70' : ''} style={!weightingOpen ? {
            color: isLight ? '#64748b' : '#64748b'
          } : undefined} data-id="element-3096">
              Weighting
            </span>
            <ChevronDown size={12} className={`transition-transform ${weightingOpen ? 'rotate-180' : ''} ${weightingOpen ? 'text-[#EC4899]' : 'text-slate-500'}`} data-id="element-3097" />
          </button>
          {weightingOpen && ReactDOM.createPortal(<div ref={weightingDropdownRef} className="fixed rounded-lg border py-1 min-w-[160px] shadow-2xl shadow-black/50 z-[9999]" style={{
          backgroundColor: isLight ? '#ffffff' : '#1e293b',
          borderColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
          top: weightingRef.current ? weightingRef.current.getBoundingClientRect().bottom + 4 : 0,
          left: weightingRef.current ? weightingRef.current.getBoundingClientRect().left : 0
        }} data-id="element-3098">
                {weightingConfig.options.map(opt => <button key={opt} onClick={() => {
            setWeighting(opt);
            setWeightingOpen(false);
          }} className="w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center justify-between" style={{
            color: opt === activeWeighting ? '#EC4899' : isLight ? '#334155' : '#e2e8f0',
            backgroundColor: opt === activeWeighting ? isLight ? 'rgba(236,72,153,0.06)' : 'rgba(236,72,153,0.1)' : 'transparent'
          }} onMouseEnter={e => {
            if (opt !== activeWeighting) {
              e.currentTarget.style.backgroundColor = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)';
            }
          }} onMouseLeave={e => {
            if (opt !== activeWeighting) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }} data-id="element-3099">
                    <span data-id="element-3100">{opt}</span>
                    {opt === activeWeighting && <span className="text-[#EC4899]" data-id="element-3101">✓</span>}
                  </button>)}
              </div>, document.body)}
        </div>

        <div className="hidden sm:block h-4 w-px flex-shrink-0" style={{
        backgroundColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(148,163,184,0.15)'
      }} data-id="element-3102" />

        {/* Model Runs — pre-selects latest runs for all models */}
        <ModelRunSelector hideOverride onOverrideChange={setModelOverride} selectedRuns={sharedSelectedRuns} onSelectedRunsChange={setSharedSelectedRuns} hiddenModels={sharedHiddenModels} onHiddenModelsChange={setSharedHiddenModels} data-id="element-3103" />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-4 gap-4 min-h-0" data-id="element-3104">
        {/* Grid Layout */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-0 pb-4 overflow-hidden" data-id="element-3105">
          {/* Left Column: Map + Model Selection - Hidden on mobile/tablet */}
          <div className="hidden lg:flex lg:col-span-4 flex-col gap-4 min-h-0 overflow-y-auto" data-id="element-3106">
            {/* Map — Location selector */}
            <div className="min-h-[280px] max-h-[380px] flex-1 short:hidden" data-id="element-3107">
              <MarketZoneMap isLight={isLight} defaultRegionId="de" onSelect={sel => {
              if (sel) {
                setLocationOverride({
                  regionId: sel.regionId,
                  regionName: sel.regionName,
                  regionType: sel.regionType
                } as any);
              } else {
                setLocationOverride(null);
              }
            }} data-id="element-3108" />
            </div>

            {/* Model + Run Selection */}
            <div className="flex-shrink-0" data-id="element-3109">
              <ModelRunSelector inline hideOverride onOverrideChange={setModelOverride} selectedRuns={sharedSelectedRuns} onSelectedRunsChange={setSharedSelectedRuns} hiddenModels={sharedHiddenModels} onHiddenModelsChange={setSharedHiddenModels} data-id="element-3110" />
            </div>
          </div>

          {/* Right Column: Forecast Chart + Tabbed Delta/Heatmap */}
          <div className="col-span-1 lg:col-span-8 flex flex-col gap-4 min-h-0 overflow-hidden" data-id="element-3111">
            <Card className="p-4 flex flex-col flex-[6] min-h-0 overflow-hidden" data-id="element-3112">
              <div className="flex items-center justify-between mb-2" data-id="element-3113">
                <div className="flex items-center gap-2" data-id="element-3114">
                  <h3 className="text-sm font-semibold" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-3115">
                    {varConfig.label} - {activeLocation}
                  </h3>
                  <Info size={14} style={{
                  color: 'var(--text-muted)'
                }} data-id="element-3116" />
                </div>
                <div className="flex items-center gap-2" data-id="element-3117">
                  {/* Models dropdown */}
                  <div className="relative flex-shrink-0" ref={chartModelsRef} data-id="element-3118">
                    <button onClick={() => setChartModelsOpen(!chartModelsOpen)} className={`jua-toolbar-btn flex items-center gap-1 h-6 px-2 rounded-md text-[10px] font-semibold border transition-all ${chartModelsOpen ? 'bg-[#EC4899]/10 border-[#EC4899]/30 text-[#EC4899]' : isLight ? 'bg-black/[0.03] border-black/[0.08] text-slate-500 hover:bg-black/[0.06] hover:text-slate-700' : 'bg-white/[0.04] border-white/[0.08] text-slate-400 hover:bg-white/[0.08] hover:text-slate-200'}`} title="Toggle models" data-id="element-3119">
                      <Cpu size={10} data-id="element-3120" />
                      <span data-id="element-3121">
                        Models (
                        {MODEL_CONFIGS.filter(m => !sharedHiddenModels.has(m.id)).length}
                        /{MODEL_CONFIGS.length})
                      </span>
                      <ChevronDown size={9} className={`transition-transform ${chartModelsOpen ? 'rotate-180' : ''}`} data-id="element-3122" />
                    </button>
                    {chartModelsOpen && ReactDOM.createPortal(<div ref={chartModelsDropdownRef} className={`fixed rounded-lg border shadow-2xl min-w-[200px] flex flex-col ${isLight ? 'border-black/[0.08] shadow-black/10' : 'border-white/[0.08] shadow-black/60'}`} style={{
                    zIndex: 10001,
                    background: isLight ? 'rgba(255, 255, 255, 0.97)' : 'rgba(11, 17, 32, 0.97)',
                    backdropFilter: 'blur(20px)',
                    maxHeight: 'min(400px, 50vh)',
                    overflowY: 'hidden',
                    ...(chartModelsRef.current ? (() => {
                      const rect = chartModelsRef.current!.getBoundingClientRect();
                      return {
                        left: Math.min(rect.left, window.innerWidth - 220),
                        top: rect.bottom + 6
                      };
                    })() : {})
                  }} data-id="element-3123">
                          <button onClick={() => {
                      if (sharedHiddenModels.size === 0) {
                        setSharedHiddenModels(new Set(MODEL_CONFIGS.slice(1).map(m => m.id)));
                      } else {
                        setSharedHiddenModels(new Set());
                      }
                    }} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors border-b ${isLight ? 'hover:bg-black/[0.04] border-black/[0.06]' : 'hover:bg-white/[0.06] border-white/[0.06]'}`} data-id="element-3124">
                            <div className={`w-[16px] h-[16px] rounded-[3px] border flex items-center justify-center transition-all flex-shrink-0 ${sharedHiddenModels.size === 0 ? 'bg-[#EC4899] border-transparent' : isLight ? 'border-slate-300' : 'border-slate-600'}`} data-id="element-3125">
                              {sharedHiddenModels.size === 0 && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="text-white" data-id="element-3126">
                                  <polyline points="20 6 9 17 4 12" data-id="element-3127" />
                                </svg>}
                            </div>
                            <span className={`text-xs font-medium ${isLight ? 'text-slate-700' : 'text-slate-200'}`} data-id="element-3128">
                              All Models
                            </span>
                          </button>
                          <div className="py-1 overflow-y-auto flex-1 min-h-0" data-id="element-3129">
                            {MODEL_CONFIGS.map(model => {
                        const isVisible = !sharedHiddenModels.has(model.id);
                        return <button key={model.id} onClick={() => {
                          const next = new Set(sharedHiddenModels);
                          if (isVisible) {
                            next.add(model.id);
                            setSharedSelectedRuns(sharedSelectedRuns.filter(r => r.modelId !== model.id));
                          } else {
                            next.delete(model.id);
                            const now = new Date();
                            const isHourly = model.id === 'ept-2-hrrr';
                            const d = new Date(now);
                            if (isHourly) {
                              d.setUTCMinutes(0, 0, 0);
                            } else {
                              d.setUTCHours(Math.floor(d.getUTCHours() / 6) * 6, 0, 0, 0);
                            }
                            const alreadyHasRun = sharedSelectedRuns.some(r => r.modelId === model.id);
                            if (!alreadyHasRun) {
                              setSharedSelectedRuns([...sharedSelectedRuns, {
                                modelId: model.id,
                                timestamp: d.getTime()
                              }]);
                            }
                          }
                          setSharedHiddenModels(next);
                        }} className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors ${isLight ? 'hover:bg-black/[0.06]' : 'hover:bg-white/[0.08]'}`} data-id="element-3130">
                                  <div className="w-[16px] h-[16px] rounded-[3px] border flex items-center justify-center transition-all flex-shrink-0" style={{
                            backgroundColor: isVisible ? model.color : 'transparent',
                            borderColor: isVisible ? 'transparent' : isLight ? 'rgba(148,163,184,0.7)' : 'rgba(148,163,184,0.35)'
                          }} data-id="element-3131">
                                    {isVisible && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="text-white" data-id="element-3132">
                                        <polyline points="20 6 9 17 4 12" data-id="element-3133" />
                                      </svg>}
                                  </div>
                                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                            backgroundColor: model.color,
                            opacity: isVisible ? 1 : 0.65
                          }} data-id="element-3134" />
                                  <span className="text-xs font-medium flex-1" style={{
                            color: isVisible ? isLight ? '#334155' : '#e2e8f0' : isLight ? '#64748b' : '#94a3b8'
                          }} data-id="element-3135">
                                    {model.name}
                                  </span>
                                  {model.badge && <Badge variant={model.badgeColor || 'default'} className="w-fit px-1 py-0 text-[7px] h-3" data-id="element-3136">
                                      {model.badge}
                                    </Badge>}
                                </button>;
                      })}
                          </div>
                        </div>, document.body)}
                  </div>
                  {/* Inline timeframe pills */}
                  <div className="flex items-center rounded-lg p-0.5 gap-0.5" style={{
                  backgroundColor: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'
                }} data-id="element-3137">
                    {[{
                    id: 'id',
                    label: 'ID',
                    hours: 24
                  }, {
                    id: 'da',
                    label: 'DA',
                    hours: 48
                  }, {
                    id: 'wk',
                    label: 'WK',
                    hours: 168
                  }, {
                    id: 'mn',
                    label: 'MN',
                    hours: 720
                  }].map(tf => {
                    const currentId = (timeframeOverride as any)?.timeframeId;
                    const isActive = currentId === tf.id || !currentId && tf.id === 'wk';
                    return <button key={tf.id} onClick={() => setTimeframeOverride({
                      timeframeId: tf.id,
                      label: tf.label
                    } as any)} className="px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all" style={{
                      backgroundColor: isActive ? 'rgba(236,72,153,0.15)' : 'transparent',
                      color: isActive ? '#EC4899' : isLight ? '#64748b' : '#94a3b8'
                    }} data-id="element-3138">
                          {tf.label}
                        </button>;
                  })}
                  </div>
                </div>
              </div>
              {/* Legend */}
              <div className="flex items-start gap-3 mb-3" data-id="element-3139">
                <div className="flex-1 flex items-center gap-2 flex-wrap min-w-0" data-id="element-3140">
                  {chartMode === 'data' && chartEntries.length > 1 && <div className="flex items-center gap-1.5 mr-1" data-id="element-3141">
                      <div className="relative" ref={groupByDropdownRef} data-id="element-3142">
                        <button onClick={() => setGroupByDropdownOpen(!groupByDropdownOpen)} className={`jua-toolbar-btn flex items-center gap-1.5 text-[10px] font-semibold h-7 px-2 rounded-lg cursor-pointer outline-none border transition-all ${groupByDropdownOpen ? 'bg-[#EC4899]/10 border-[#EC4899]/30 text-[#EC4899]' : isLight ? 'bg-black/[0.03] border-black/[0.08] text-slate-600 hover:bg-black/[0.06] hover:text-slate-900' : 'bg-white/[0.04] border-white/[0.08] text-slate-300 hover:bg-white/[0.08] hover:text-white'}`} data-id="element-3143">
                          <span className="capitalize" data-id="element-3144">Legend grouping</span>
                          <ChevronDown size={10} className="flex-shrink-0 transition-transform" style={{
                        color: isLight ? '#94a3b8' : '#64748b',
                        transform: groupByDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                      }} data-id="element-3145" />
                        </button>
                        {groupByDropdownOpen && <>
                            <div className="fixed inset-0 z-40" onClick={() => setGroupByDropdownOpen(false)} data-id="element-3146" />
                            <div className="absolute top-full left-0 mt-1 z-50 min-w-[120px] rounded-lg py-1 overflow-hidden" style={{
                        background: isLight ? 'rgba(255,255,255,0.98)' : 'rgba(15, 23, 42, 0.96)',
                        border: `1px solid ${isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
                        backdropFilter: 'blur(20px)',
                        boxShadow: isLight ? '0 8px 30px rgba(0,0,0,0.12)' : '0 8px 30px rgba(0,0,0,0.5)'
                      }} data-id="element-3147">
                              <div className="px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-wider" style={{
                          color: isLight ? '#94a3b8' : '#475569'
                        }} data-id="element-3148">
                                Group legend by
                              </div>
                              {(['model', 'run', 'location'] as const).map(mode => {
                          const isSelected = legendGroupBy === mode;
                          return <button key={mode} onClick={() => {
                            setLegendGroupBy(mode);
                            setGroupByDropdownOpen(false);
                          }} className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left transition-colors capitalize" style={{
                            backgroundColor: isSelected ? isLight ? 'rgba(236,72,153,0.08)' : 'rgba(236,72,153,0.12)' : 'transparent',
                            color: isSelected ? '#EC4899' : isLight ? '#334155' : '#e2e8f0'
                          }} onMouseEnter={e => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)';
                            }
                          }} onMouseLeave={e => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }} data-id="element-3149">
                                      <span className="text-[11px] font-medium" data-id="element-3150">
                                        {mode}
                                      </span>
                                    </button>;
                        })}
                            </div>
                          </>}
                      </div>
                    </div>}
                  {chartMode === 'data' ? <>
                      {legendGroups.map(group => {
                    const allGroupHidden = group.keys.every(k => hiddenKeys.has(k));
                    const isGroupHovered = hoveredGroup === group.label;
                    const hasMultiple = group.entries.length > 1;
                    return <div key={group.label} className="flex items-center gap-1 rounded-md transition-all duration-150" style={{
                      padding: hasMultiple ? '2px 6px 2px 4px' : '0',
                      backgroundColor: hasMultiple ? isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)' : 'transparent',
                      border: hasMultiple ? `1px solid ${isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}` : 'none',
                      opacity: allGroupHidden ? 0.4 : 1
                    }} data-id="element-3151">
                            {/* Group header — clickable to toggle all */}
                            {hasMultiple && <button className="flex items-center gap-1 mr-1 cursor-pointer select-none" onClick={() => toggleGroup(group.keys)} onMouseEnter={() => setHoveredGroup(group.label)} onMouseLeave={() => setHoveredGroup(null)} title={allGroupHidden ? `Show all ${group.label}` : `Hide all ${group.label}`} data-id="element-3152">
                                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                          backgroundColor: group.color
                        }} data-id="element-3153" />
                                <span className="text-[10px] font-semibold" style={{
                          color: isLight ? '#475569' : '#94a3b8'
                        }} data-id="element-3154">
                                  {group.label}
                                </span>
                                <span className="text-[9px]" style={{
                          color: isLight ? '#94a3b8' : '#475569'
                        }} data-id="element-3155">
                                  ·
                                </span>
                              </button>}
                            {/* Individual entries */}
                            {group.entries.map(entry => {
                        const displayName = `${entry.modelName} · ${entry.runLabel}`;
                        const isHidden = hiddenKeys.has(entry.key);
                        const isFaded = hoveredKey !== null && hoveredKey !== entry.key || hoveredGroup !== null && !group.keys.includes(entry.key);
                        return <button key={entry.key} className="flex items-center gap-1 cursor-pointer select-none transition-opacity duration-150" style={{
                          opacity: isHidden ? 0.3 : isFaded ? 0.4 : 1
                        }} onClick={() => toggleLine(entry.key)} onMouseEnter={() => setHoveredKey(entry.key)} onMouseLeave={() => setHoveredKey(null)} title={isHidden ? `Show ${displayName}` : `Hide ${displayName}`} data-id="element-3156">
                                  <div className="w-4 h-[2px] rounded-full" style={{
                            backgroundColor: isHidden ? isLight ? '#cbd5e1' : '#475569' : entry.color,
                            opacity: isHidden ? 1 : entry.ageOpacity
                          }} data-id="element-3157" />
                                  <span className="text-[10px] font-medium" style={{
                            color: isHidden ? isLight ? '#94a3b8' : '#475569' : 'var(--text-secondary)',
                            textDecoration: isHidden ? 'line-through' : 'none',
                            opacity: isHidden ? 1 : entry.ageOpacity
                          }} data-id="element-3158">
                                    {displayName}
                                  </span>
                                </button>;
                      })}
                          </div>;
                  })}
                    </> : <>
                      {chartEntries.length >= 2 && baselineEntry ? <>
                          {/* Baseline selector */}
                          <div className="flex items-center gap-1.5" data-id="element-3159">
                            <span className="text-[10px] font-medium" style={{
                        color: isLight ? '#94a3b8' : '#64748b'
                      }} data-id="element-3160">
                              Baseline:
                            </span>
                            <div className="relative" ref={baselineDropdownRef} data-id="element-3161">
                              <button onClick={() => setBaselineDropdownOpen(!baselineDropdownOpen)} className={`jua-toolbar-btn flex items-center gap-1.5 text-[10px] font-semibold h-7 px-2 rounded-lg cursor-pointer outline-none border transition-all ${baselineDropdownOpen ? 'bg-[#EC4899]/10 border-[#EC4899]/30 text-[#EC4899]' : 'bg-white/[0.04] border-white/[0.08] text-slate-300 hover:bg-white/[0.08] hover:text-white'}`} data-id="element-3162">
                                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                            backgroundColor: baselineEntry.color
                          }} data-id="element-3163" />
                                <span data-id="element-3164">
                                  {baselineEntry.modelName} (
                                  {baselineEntry.runLabel})
                                </span>
                                <ChevronDown size={10} className="flex-shrink-0 transition-transform" style={{
                            color: isLight ? '#94a3b8' : '#64748b',
                            transform: baselineDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                          }} data-id="element-3165" />
                              </button>
                              {baselineDropdownOpen && <>
                                  <div className="fixed inset-0 z-40" onClick={() => setBaselineDropdownOpen(false)} data-id="element-3166" />
                                  <div className="absolute top-full left-0 mt-1 z-50 min-w-[180px] rounded-lg py-1 overflow-hidden" style={{
                            background: isLight ? 'rgba(255,255,255,0.98)' : 'rgba(15, 23, 42, 0.96)',
                            border: `1px solid ${isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
                            backdropFilter: 'blur(20px)',
                            boxShadow: isLight ? '0 8px 30px rgba(0,0,0,0.12)' : '0 8px 30px rgba(0,0,0,0.5)'
                          }} data-id="element-3167">
                                    <div className="px-2.5 py-1.5 text-[9px] font-semibold uppercase tracking-wider" style={{
                              color: isLight ? '#94a3b8' : '#475569'
                            }} data-id="element-3168">
                                      Select baseline run
                                    </div>
                                    {chartEntries.map(entry => {
                              const isSelected = entry.key === baselineEntry.key;
                              return <button key={entry.key} onClick={() => {
                                setBaselineKey(entry.key);
                                setBaselineDropdownOpen(false);
                              }} className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left transition-colors" style={{
                                backgroundColor: isSelected ? isLight ? 'rgba(236,72,153,0.08)' : 'rgba(236,72,153,0.12)' : 'transparent',
                                color: isSelected ? '#EC4899' : isLight ? '#334155' : '#e2e8f0'
                              }} onMouseEnter={e => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)';
                                }
                              }} onMouseLeave={e => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }
                              }} data-id="element-3169">
                                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                                  backgroundColor: entry.color,
                                  opacity: entry.ageOpacity
                                }} data-id="element-3170" />
                                          <span className="text-[11px] font-medium flex-1" data-id="element-3171">
                                            {entry.modelName}
                                          </span>
                                          <span className="text-[10px] font-mono" style={{
                                  color: isLight ? '#94a3b8' : '#64748b'
                                }} data-id="element-3172">
                                            {entry.runLabel}
                                          </span>
                                          {isSelected && <div className="w-1 h-1 rounded-full flex-shrink-0" style={{
                                  backgroundColor: '#EC4899'
                                }} data-id="element-3173" />}
                                        </button>;
                            })}
                                  </div>
                                </>}
                            </div>
                          </div>
                          {/* Separator */}
                          <div className="w-px h-3 mx-1" style={{
                      backgroundColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'
                    }} data-id="element-3174" />
                          {/* Delta entries */}
                          {deltaEntries.map(entry => {
                      const displayName = entry.sameModelCount > 1 ? `${entry.modelName} (${entry.runLabel})` : entry.modelName;
                      return <div key={entry.key} className="flex items-center gap-1.5" data-id="element-3175">
                                <div className="w-4 h-[2px] rounded-full" style={{
                          backgroundColor: entry.color
                        }} data-id="element-3176" />
                                <span className="text-[10px] font-medium" style={{
                          color: 'var(--text-secondary)'
                        }} data-id="element-3177">
                                  Δ {displayName}
                                </span>
                              </div>;
                    })}
                          {/* Direction indicators */}
                          <div className="flex items-center gap-1.5 ml-1" data-id="element-3178">
                            <span className="text-[9px]" style={{
                        color: '#10b981'
                      }} data-id="element-3179">
                              ▲ higher
                            </span>
                            <span className="text-[9px]" style={{
                        color: '#ef4444'
                      }} data-id="element-3180">
                              ▼ lower
                            </span>
                            <span className="text-[9px]" style={{
                        color: isLight ? '#94a3b8' : '#64748b'
                      }} data-id="element-3181">
                              vs baseline
                            </span>
                          </div>
                        </> : <span className="text-[11px]" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-3182">
                          Select at least 2 model runs to see delta
                        </span>}
                    </>}
                </div>
              </div>
              <div className="flex-1 min-h-[150px] relative" style={{
              cursor: isZoomed ? 'grab' : 'crosshair',
              userSelect: 'none',
              WebkitUserSelect: 'none'
            }} ref={chartContainerRef} onMouseDown={handleChartMouseDown} data-id="element-3183">
                {/* Top-right chart controls */}
                <div className="absolute top-2 right-2 z-20 flex items-center gap-1.5" data-id="element-3184">
                  {/* Recent Observations toggle */}
                  <button onClick={e => {
                  e.stopPropagation();
                  setShowObservations(!showObservations);
                }} onMouseDown={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium border transition-all" style={{
                  background: showObservations ? isLight ? 'rgba(236,72,153,0.1)' : 'rgba(236,72,153,0.15)' : isLight ? 'rgba(255,255,255,0.9)' : 'rgba(15,23,42,0.9)',
                  borderColor: showObservations ? 'rgba(236,72,153,0.35)' : isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                  color: showObservations ? '#EC4899' : isLight ? '#64748b' : '#94a3b8',
                  backdropFilter: 'blur(8px)'
                }} title={showObservations ? 'Hide observations' : 'Show recent observations'} data-id="element-3185">
                    <ActivityIcon size={10} data-id="element-3186" />
                    {showObservations ? 'Observations ON' : 'Recent observations'}
                  </button>
                  {/* Reset zoom */}
                  {isZoomed && <button onClick={e => {
                  e.stopPropagation();
                  resetZoom();
                }} onMouseDown={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium border transition-all" style={{
                  background: isLight ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.45)',
                  borderColor: isLight ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.08)',
                  color: isLight ? '#64748b' : '#94a3b8',
                  backdropFilter: 'blur(16px) saturate(1.4)',
                  WebkitBackdropFilter: 'blur(16px) saturate(1.4)',
                  boxShadow: isLight ? '0 4px 16px rgba(0,0,0,0.06), 0 0 0 1px rgba(255,255,255,0.4) inset' : '0 4px 16px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.05) inset'
                }} data-id="element-3187">
                      <ZoomOut size={10} data-id="element-3188" />
                      Reset zoom
                    </button>}
                </div>
                <ResponsiveContainer width="100%" height="100%" debounce={100} data-id="element-3189">
                  {chartMode === 'data' ? <LineChart syncId="energy-market-sync" data={chartData} margin={{
                  top: 4,
                  right: 4,
                  bottom: 8,
                  left: 0
                }} data-id="element-3190">
                      <defs data-id="element-3191">
                        {chartEntries.map(entry => <filter key={`glow-${entry.key}`} id={`glow-${entry.key}`} x="-20%" y="-20%" width="140%" height="140%" data-id="element-3192">
                            <feGaussianBlur in="SourceGraphic" stdDeviation={isLight ? 2 : 3.5} result="blur" data-id="element-3193" />
                            <feColorMatrix in="blur" type="matrix" values={`1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 ${isLight ? 0.25 : 0.5} 0`} result="glow" data-id="element-3194" />
                            <feMerge data-id="element-3195">
                              <feMergeNode in="glow" data-id="element-3196" />
                              <feMergeNode in="SourceGraphic" data-id="element-3197" />
                            </feMerge>
                          </filter>)}
                      </defs>
                      {/* Observations background shading — animated fade-in */}
                      {showObservations && <ReferenceArea x1={-48} x2={0} fill={isLight ? 'rgba(236,72,153,0.04)' : 'rgba(236,72,153,0.06)'} fillOpacity={1} ifOverflow="extendDomain" label={(props: any) => {
                    const {
                      viewBox
                    } = props;
                    if (!viewBox) return null;
                    const x = viewBox.x + 8;
                    const y = viewBox.y + 14;
                    return <g data-id="element-3199">
                                <text x={x} y={y} fill={isLight ? 'rgba(236,72,153,0.4)' : 'rgba(236,72,153,0.35)'} fontSize={9} fontWeight={600} opacity={0} data-id="element-3200">
                                  OBSERVATIONS
                                  <animate attributeName="opacity" from="0" to="1" dur="0.6s" fill="freeze" begin="0.15s" data-id="element-3201" />
                                </text>
                              </g>;
                  }} data-id="element-3198" />}
                      {/* Weekend shading */}
                      {weekendRanges.map((range, i) => <ReferenceArea key={`weekend-${i}`} x1={range.start} x2={range.end} fill={isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)'} fillOpacity={1} ifOverflow="extendDomain" data-id="element-3202" />)}
                      <CartesianGrid strokeDasharray="3 3" stroke={isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)'} vertical={false} data-id="element-3203" />
                      {/* Midnight markers */}
                      {midnightHours.map(m => <ReferenceLine key={`midnight-${m.hour}`} x={m.hour} stroke={isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.15)'} strokeWidth={1} strokeDasharray="4 2" label={(props: any) => {
                    const {
                      viewBox
                    } = props;
                    const x = viewBox?.x ?? 0;
                    const y = viewBox?.y ?? 0;
                    return <g data-id="element-3205">
                                <text x={x + 4} y={y + 12} fill={isLight ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.2)'} fontSize={9} fontWeight={700} data-id="element-3206">
                                  {m.dow}
                                </text>
                                <text x={x} y={y - 4} fill={isLight ? '#94a3b8' : '#475569'} fontSize={9} fontWeight={600} textAnchor="middle" data-id="element-3207">
                                  {m.label}
                                </text>
                              </g>;
                  }} data-id="element-3204" />)}
                      <XAxis dataKey="hour" stroke={isLight ? '#94a3b8' : '#475569'} fontSize={10} axisLine={false} tickLine={false} tickFormatter={formatHourTick} height={22} data-id="element-3208" />
                      <YAxis stroke={isLight ? '#94a3b8' : '#475569'} fontSize={10} axisLine={false} tickLine={false} tickFormatter={(v: number) => v.toFixed(1)} width={40} domain={[`dataMin - ${yPadding}`, `dataMax + ${yPadding}`]} tickCount={8} data-id="element-3209" />
                      <Tooltip isAnimationActive={false} animationDuration={0} cursor={<CrosshairCursor stroke={isLight ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.35)'} strokeDasharray="4 3" data-id="element-3211" />} content={({
                    active,
                    payload,
                    label
                  }) => {
                    if (!active || !payload?.length) return null;
                    const now = new Date();
                    const target = new Date(now.getTime() + Math.round(label as number) * 3600000);
                    const dow = shortDow(target);
                    const dateStr = target.toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    });
                    const hourStr = `${String(target.getHours()).padStart(2, '0')}:00`;
                    return <div className="rounded-xl px-4 py-3 border shadow-2xl min-w-[160px]" style={{
                      background: isLight ? 'rgba(255,255,255,0.58)' : 'rgba(15, 23, 42, 0.5)',
                      borderColor: isLight ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(20px) saturate(1.6)',
                      WebkitBackdropFilter: 'blur(20px) saturate(1.6)',
                      boxShadow: isLight ? '0 8px 32px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.6) inset' : '0 8px 32px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.07) inset'
                    }} data-id="element-3212">
                              <div className="mb-2.5" data-id="element-3213">
                                <div className="text-[13px] font-bold" style={{
                          color: isLight ? '#0f172a' : '#ffffff'
                        }} data-id="element-3214">
                                  {dow} {dateStr}
                                </div>
                                <div className="text-[11px] font-medium" style={{
                          color: isLight ? '#64748b' : '#94a3b8'
                        }} data-id="element-3215">
                                  {hourStr}
                                </div>
                              </div>
                              <div className="space-y-2" data-id="element-3216">
                                {/* Observations entry in tooltip */}
                                {(() => {
                          const obsItem = payload.find((item: any) => item.dataKey === 'observations');
                          if (obsItem && obsItem.value != null) {
                            return <div data-id="element-3217">
                                        <div className="flex items-center gap-1.5 mb-1" data-id="element-3218">
                                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                                  backgroundColor: isLight ? '#0f172a' : '#f8fafc'
                                }} data-id="element-3219" />
                                          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{
                                  color: isLight ? '#94a3b8' : '#64748b'
                                }} data-id="element-3220">
                                            Observed
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between gap-5" style={{
                                paddingLeft: 8
                              }} data-id="element-3221">
                                          <div className="flex items-center gap-2" data-id="element-3222">
                                            <div className="w-2 h-[2px] rounded-full flex-shrink-0" style={{
                                    backgroundColor: isLight ? '#0f172a' : '#f8fafc'
                                  }} data-id="element-3223" />
                                            <span className="text-[11px] font-medium" style={{
                                    color: isLight ? '#64748b' : '#cbd5e1'
                                  }} data-id="element-3224">
                                              Station data
                                            </span>
                                          </div>
                                          <div className="flex items-baseline gap-1" data-id="element-3225">
                                            <span className="text-[13px] font-bold tabular-nums" style={{
                                    color: isLight ? '#0f172a' : '#ffffff'
                                  }} data-id="element-3226">
                                              {typeof obsItem.value === 'number' ? obsItem.value.toFixed(1) : obsItem.value}
                                            </span>
                                            <span className="text-[10px] font-medium" style={{
                                    color: isLight ? '#94a3b8' : '#64748b'
                                  }} data-id="element-3227">
                                              {varConfig.unit}
                                            </span>
                                          </div>
                                        </div>
                                      </div>;
                          }
                          return null;
                        })()}
                                {legendGroups.map(group => {
                          const groupItems = payload.filter((item: any) => group.keys.includes(item.dataKey));
                          if (groupItems.length === 0) return null;
                          return <div key={group.label} data-id="element-3228">
                                      <div className="flex items-center gap-1.5 mb-1" data-id="element-3229">
                                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                                backgroundColor: group.color
                              }} data-id="element-3230" />
                                        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{
                                color: isLight ? '#94a3b8' : '#64748b'
                              }} data-id="element-3231">
                                          {group.label}
                                        </span>
                                      </div>
                                      <div className="space-y-1" data-id="element-3232">
                                        {groupItems.map((item: any) => {
                                const entry = chartEntries.find(e => e.key === item.dataKey);
                                const displayName = entry ? `${entry.modelName} · ${entry.runLabel}` : item.name || item.dataKey;
                                return <div key={item.dataKey} className="flex items-center justify-between gap-5" style={{
                                  paddingLeft: 8
                                }} data-id="element-3233">
                                              <div className="flex items-center gap-2" data-id="element-3234">
                                                <div className="w-2 h-[2px] rounded-full flex-shrink-0" style={{
                                      backgroundColor: item.color,
                                      opacity: entry?.ageOpacity ?? 1
                                    }} data-id="element-3235" />
                                                <span className="text-[11px] font-medium" style={{
                                      color: isLight ? '#64748b' : '#cbd5e1',
                                      opacity: entry?.ageOpacity ?? 1
                                    }} data-id="element-3236">
                                                  {displayName}
                                                </span>
                                              </div>
                                              <div className="flex items-baseline gap-1" data-id="element-3237">
                                                <span className="text-[13px] font-bold tabular-nums" style={{
                                      color: isLight ? '#0f172a' : '#ffffff'
                                    }} data-id="element-3238">
                                                  {typeof item.value === 'number' ? item.value.toFixed(1) : item.value}
                                                </span>
                                                <span className="text-[10px] font-medium" style={{
                                      color: isLight ? '#94a3b8' : '#64748b'
                                    }} data-id="element-3239">
                                                  {varConfig.unit}
                                                </span>
                                              </div>
                                            </div>;
                              })}
                                      </div>
                                    </div>;
                        })}
                              </div>
                            </div>;
                  }} data-id="element-3210" />
                      {chartEntries.map(entry => {
                    const isHidden = hiddenKeys.has(entry.key);
                    const isEntryHovered = hoveredKey === entry.key;
                    // Check if this entry's group is hovered
                    const entryGroup = legendGroups.find(g => g.keys.includes(entry.key));
                    const isGroupHovered = hoveredGroup !== null && entryGroup?.label === hoveredGroup;
                    const isFaded = hoveredKey !== null && !isEntryHovered || hoveredGroup !== null && !isGroupHovered;
                    // Compute final opacity: age × fade/hidden
                    const baseOpacity = entry.ageOpacity;
                    const finalOpacity = isHidden ? 0 : isFaded ? baseOpacity * 0.45 : baseOpacity;
                    // Vary stroke width: newest = 1.2, older = thinner
                    const baseWidth = entry.sameModelCount > 1 ? Math.max(0.7, 1.2 - entry.ageIndex * 0.15) : 1.2;
                    const strokeW = isEntryHovered || isGroupHovered ? baseWidth + 0.5 : baseWidth;
                    return <Line key={entry.key} type="monotone" dataKey={entry.key} name={entry.sameModelCount > 1 ? `${entry.modelName} (${entry.runLabel})` : entry.modelName} stroke={entry.color} strokeWidth={strokeW} strokeOpacity={finalOpacity} filter={!isHidden && finalOpacity > 0.3 ? `url(#glow-${entry.key})` : undefined} dot={false} isAnimationActive={false} activeDot={isHidden ? false : (props: any) => {
                      const {
                        cx,
                        cy
                      } = props;
                      return <g data-id="element-3241">
                                        {/* Outer glow ring */}
                                        <circle cx={cx} cy={cy} r={6} fill={entry.color} fillOpacity={0.15} data-id="element-3242" />
                                        {/* Crosshair lines */}
                                        <line x1={cx - 5} y1={cy} x2={cx + 5} y2={cy} stroke={entry.color} strokeWidth={0.8} strokeOpacity={0.5} data-id="element-3243" />
                                        <line x1={cx} y1={cy - 5} x2={cx} y2={cy + 5} stroke={entry.color} strokeWidth={0.8} strokeOpacity={0.5} data-id="element-3244" />
                                        {/* Diamond shape */}
                                        <path d={`M${cx},${cy - 3.5} L${cx + 3.5},${cy} L${cx},${cy + 3.5} L${cx - 3.5},${cy} Z`} fill={entry.color} stroke={isLight ? '#fff' : '#0f172a'} strokeWidth={1.5} data-id="element-3245" />
                                      </g>;
                    }} hide={isHidden} data-id="element-3240" />;
                  })}
                      {/* Observations line — smooth draw-in animation */}
                      {showObservations && <Line type="monotone" dataKey="observations" name="Observations" stroke={isLight ? '#0f172a' : '#f8fafc'} strokeWidth={2} strokeOpacity={0.8} dot={false} isAnimationActive={true} animationDuration={600} animationBegin={0} animationEasing="ease-out" activeDot={{
                    r: 4,
                    fill: isLight ? '#0f172a' : '#f8fafc',
                    stroke: isLight ? '#fff' : '#0f172a',
                    strokeWidth: 2
                  }} connectNulls={false} data-id="element-3246" />}
                      {/* "Now" reference line at h=0 when observations are showing */}
                      {showObservations && <ReferenceLine x={0} stroke="#EC4899" strokeWidth={1.5} strokeDasharray="6 3" label={(props: any) => {
                    const {
                      viewBox
                    } = props;
                    const x = viewBox?.x ?? 0;
                    const y = viewBox?.y ?? 0;
                    return <g opacity={0} data-id="element-3248">
                                <animate attributeName="opacity" from="0" to="1" dur="0.5s" fill="freeze" begin="0.3s" data-id="element-3249" />
                                <rect x={x - 14} y={y - 18} width={28} height={14} rx={3} fill="#EC4899" fillOpacity={isLight ? 0.12 : 0.2} data-id="element-3250" />
                                <text x={x} y={y - 8} fill="#EC4899" fontSize={9} fontWeight={700} textAnchor="middle" data-id="element-3251">
                                  NOW
                                </text>
                              </g>;
                  }} data-id="element-3247" />}
                      <Brush key={`brush-${brushKey}`} dataKey="hour" height={20} stroke={isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)'} fill={isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)'} travellerWidth={8} startIndex={brushRange?.startIndex ?? 0} endIndex={brushRange?.endIndex ?? chartData.length - 1} onChange={handleBrushChange} className="recharts-brush-scrollbar" traveller={(props: any) => {
                    const {
                      x,
                      y,
                      width,
                      height
                    } = props;
                    const h = height;
                    const cx = x + width / 2;
                    return <g style={{
                      cursor: 'ew-resize'
                    }} data-id="element-3253">
                              <rect x={x} y={y} width={width} height={h} rx={3} ry={3} fill={isLight ? 'rgba(236,72,153,0.25)' : 'rgba(236,72,153,0.3)'} stroke={isLight ? 'rgba(236,72,153,0.4)' : 'rgba(236,72,153,0.5)'} strokeWidth={0.5} data-id="element-3254" />
                              {/* Grip dots */}
                              {[-2, 0, 2].map(dy => <circle key={dy} cx={cx} cy={y + h / 2 + dy * 2.5} r={0.8} fill={isLight ? 'rgba(236,72,153,0.6)' : 'rgba(236,72,153,0.7)'} data-id="element-3255" />)}
                            </g>;
                  }} data-id="element-3252" />
                    </LineChart> : <LineChart data={deltaData} margin={{
                  top: 4,
                  right: 4,
                  bottom: 0,
                  left: 0
                }} data-id="element-3256">
                      <defs data-id="element-3257">
                        {deltaEntries.map(entry => <filter key={`delta-glow-${entry.key}`} id={`delta-glow-${entry.key}`} x="-20%" y="-20%" width="140%" height="140%" data-id="element-3258">
                            <feGaussianBlur in="SourceGraphic" stdDeviation={isLight ? 2 : 3} result="blur" data-id="element-3259" />
                            <feColorMatrix in="blur" type="matrix" values={`1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 ${isLight ? 0.2 : 0.4} 0`} result="glow" data-id="element-3260" />
                            <feMerge data-id="element-3261">
                              <feMergeNode in="glow" data-id="element-3262" />
                              <feMergeNode in="SourceGraphic" data-id="element-3263" />
                            </feMerge>
                          </filter>)}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={isLight ? 'rgba(0,0,0,0.08)' : '#1e293b'} vertical={false} data-id="element-3264" />
                      <XAxis dataKey="hour" stroke={isLight ? '#94a3b8' : '#475569'} fontSize={10} axisLine={false} tickLine={false} tickFormatter={formatHourTick} height={22} data-id="element-3265" />
                      <YAxis stroke={isLight ? '#94a3b8' : '#475569'} fontSize={10} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v > 0 ? '+' : ''}${v.toFixed(1)}`} width={40} domain={['dataMin - 0.3', 'dataMax + 0.3']} tickCount={8} data-id="element-3266" />
                      <ReferenceLine y={0} stroke={isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)'} strokeDasharray="4 4" data-id="element-3267" />
                      <Tooltip isAnimationActive={false} animationDuration={0} cursor={<CrosshairCursor stroke={isLight ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.35)'} strokeDasharray="4 3" data-id="element-3269" />} content={({
                    active,
                    payload,
                    label
                  }) => {
                    if (!active || !payload?.length) return null;
                    const now = new Date();
                    const target = new Date(now.getTime() + Math.round(label as number) * 3600000);
                    const dateStr = target.toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    });
                    const hourStr = `${String(target.getHours()).padStart(2, '0')}:00`;
                    return <div className="rounded-xl px-4 py-3 border shadow-2xl min-w-[180px]" style={{
                      background: isLight ? 'rgba(255,255,255,0.58)' : 'rgba(15, 23, 42, 0.5)',
                      borderColor: isLight ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(20px) saturate(1.6)',
                      WebkitBackdropFilter: 'blur(20px) saturate(1.6)',
                      boxShadow: isLight ? '0 8px 32px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.6) inset' : '0 8px 32px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.07) inset',
                      backdropFilter: 'blur(20px)'
                    }} data-id="element-3270">
                              <div className="mb-2" data-id="element-3271">
                                <div className="text-[13px] font-bold" style={{
                          color: isLight ? '#0f172a' : '#ffffff'
                        }} data-id="element-3272">
                                  {dateStr}
                                </div>
                                <div className="text-[11px] font-medium" style={{
                          color: isLight ? '#64748b' : '#94a3b8'
                        }} data-id="element-3273">
                                  {hourStr} · vs {baselineEntry?.modelName} (
                                  {baselineEntry?.runLabel})
                                </div>
                              </div>
                              <div className="space-y-2" data-id="element-3274">
                                {legendGroups.map(group => {
                          const groupItems = payload.filter((item: any) => {
                            const entryKey = (item.dataKey as string).replace('delta-', '');
                            return group.keys.includes(entryKey);
                          });
                          if (groupItems.length === 0) return null;
                          const hasMultiple = group.entries.length > 1;
                          return <div key={group.label} data-id="element-3275">
                                      {hasMultiple && <div className="flex items-center gap-1.5 mb-1" data-id="element-3276">
                                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                                backgroundColor: group.color
                              }} data-id="element-3277" />
                                          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{
                                color: isLight ? '#94a3b8' : '#64748b'
                              }} data-id="element-3278">
                                            {group.label}
                                          </span>
                                        </div>}
                                      <div className="space-y-1" data-id="element-3279">
                                        {groupItems.map((item: any) => {
                                const val = item.value as number;
                                const entryKey = (item.dataKey as string).replace('delta-', '');
                                const entry = deltaEntries.find(e => e.key === entryKey);
                                const displayName = entry ? `${entry.modelName} · ${entry.runLabel}` : item.dataKey;
                                return <div key={item.dataKey} className="flex items-center justify-between gap-4" style={{
                                  paddingLeft: hasMultiple ? 8 : 0
                                }} data-id="element-3280">
                                              <div className="flex items-center gap-1.5" data-id="element-3281">
                                                <div className="w-2 h-[2px] rounded-full flex-shrink-0" style={{
                                      backgroundColor: item.color,
                                      opacity: entry?.ageOpacity ?? 1
                                    }} data-id="element-3282" />
                                                <span className="text-[11px] font-medium" style={{
                                      color: isLight ? '#64748b' : '#cbd5e1',
                                      opacity: entry?.ageOpacity ?? 1
                                    }} data-id="element-3283">
                                                  {displayName}
                                                </span>
                                              </div>
                                              <span className="text-[12px] font-bold tabular-nums" style={{
                                    color: val >= 0 ? '#10b981' : '#ef4444'
                                  }} data-id="element-3284">
                                                {val >= 0 ? '+' : ''}
                                                {val.toFixed(2)}{' '}
                                                {varConfig.unit}
                                              </span>
                                            </div>;
                              })}
                                      </div>
                                    </div>;
                        })}
                              </div>
                            </div>;
                  }} data-id="element-3268" />
                      {deltaEntries.map(entry => <Line key={`delta-${entry.key}`} type="monotone" dataKey={`delta-${entry.key}`} name={entry.sameModelCount > 1 ? `${entry.modelName} (${entry.runLabel})` : entry.modelName} stroke={entry.color} strokeWidth={1.2} filter={`url(#delta-glow-${entry.key})`} dot={false} isAnimationActive={false} activeDot={(props: any) => {
                    const {
                      cx,
                      cy
                    } = props;
                    return <g data-id="element-3286">
                                <circle cx={cx} cy={cy} r={6} fill={entry.color} fillOpacity={0.15} data-id="element-3287" />
                                <line x1={cx - 5} y1={cy} x2={cx + 5} y2={cy} stroke={entry.color} strokeWidth={0.8} strokeOpacity={0.5} data-id="element-3288" />
                                <line x1={cx} y1={cy - 5} x2={cx} y2={cy + 5} stroke={entry.color} strokeWidth={0.8} strokeOpacity={0.5} data-id="element-3289" />
                                <path d={`M${cx},${cy - 3.5} L${cx + 3.5},${cy} L${cx},${cy + 3.5} L${cx - 3.5},${cy} Z`} fill={entry.color} stroke={isLight ? '#fff' : '#0f172a'} strokeWidth={1.5} data-id="element-3290" />
                              </g>;
                  }} data-id="element-3285" />)}
                    </LineChart>}
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="flex flex-col flex-[4] min-h-0 overflow-hidden" data-id="element-3291">
              {/* Tab bar inside card */}
              <div className="px-4 pt-3 pb-0" data-id="element-3292">
                <Tabs tabs={[{
                id: 'delta',
                label: 'Δ Delta Chart'
              }, {
                id: 'table',
                label: 'Heatmap Table'
              }]} activeTab={bottomTab} onChange={id => setBottomTab(id as 'delta' | 'table')} small data-id="element-3293" />
              </div>

              {bottomTab === 'delta' && <div className="flex flex-col flex-1 min-h-0 overflow-hidden" data-id="element-3294">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{
                borderColor: isLight ? 'var(--border-default)' : 'rgba(255,255,255,0.06)'
              }} data-id="element-3295">
                    <div className="flex items-center gap-2" data-id="element-3296">
                      <h3 className="text-sm font-semibold" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-3297">
                        Δ Forecast Delta — {varConfig.label}
                      </h3>
                      {chartEntries.length >= 2 && <div className="relative" data-delta-baseline-dropdown data-id="element-3298">
                          <button onClick={() => setBaselineDropdownOpen(!baselineDropdownOpen)} className={`jua-toolbar-btn flex items-center gap-1.5 text-[10px] font-semibold h-7 px-2 rounded-lg cursor-pointer outline-none border transition-all ${baselineDropdownOpen ? 'bg-[#EC4899]/10 border-[#EC4899]/30 text-[#EC4899]' : isLight ? 'bg-black/[0.04] border-black/[0.08] text-slate-500 hover:bg-black/[0.08] hover:text-slate-700' : 'bg-white/[0.04] border-white/[0.08] text-slate-300 hover:bg-white/[0.08] hover:text-white'}`} data-id="element-3299">
                            vs {baselineEntry?.modelName}{' '}
                            {baselineEntry?.runLabel}
                            <ChevronDown size={10} className={`transition-transform ${baselineDropdownOpen ? 'rotate-180' : ''}`} data-id="element-3300" />
                          </button>
                          {baselineDropdownOpen && <div className="absolute left-0 top-full mt-1 rounded-lg border shadow-2xl shadow-black/60 overflow-hidden z-50 min-w-[180px]" style={{
                      background: isLight ? 'rgba(255,255,255,0.98)' : 'rgba(11, 17, 32, 0.97)',
                      borderColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.08)',
                      backdropFilter: 'blur(20px)'
                    }} data-id="element-3301">
                              <div className="px-2.5 py-1.5 border-b" style={{
                        borderColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'
                      }} data-id="element-3302">
                                <span className="text-[9px] font-semibold uppercase tracking-wider" style={{
                          color: isLight ? '#94a3b8' : '#475569'
                        }} data-id="element-3303">
                                  Baseline model run
                                </span>
                              </div>
                              <div className="py-1 max-h-[200px] overflow-y-auto" data-id="element-3304">
                                {chartEntries.map(entry => {
                          const isSelected = entry.key === baselineEntry?.key;
                          return <button key={entry.key} onClick={() => {
                            setBaselineKey(entry.key);
                            setBaselineDropdownOpen(false);
                          }} className="w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors" style={{
                            backgroundColor: isSelected ? isLight ? 'rgba(236,72,153,0.08)' : 'rgba(236,72,153,0.12)' : 'transparent'
                          }} onMouseEnter={e => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)';
                          }} onMouseLeave={e => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                          }} data-id="element-3305">
                                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                              backgroundColor: entry.color
                            }} data-id="element-3306" />
                                      <span className="text-[11px] font-medium flex-1" style={{
                              color: isSelected ? '#EC4899' : isLight ? '#334155' : '#e2e8f0'
                            }} data-id="element-3307">
                                        {entry.modelName} · {entry.runLabel}
                                      </span>
                                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899]" data-id="element-3308" />}
                                    </button>;
                        })}
                              </div>
                            </div>}
                        </div>}
                    </div>
                    <span className="text-[10px] font-medium" style={{
                  color: isLight ? '#94a3b8' : '#64748b'
                }} data-id="element-3309">
                      Δ {varConfig.unit} · Hourly
                    </span>
                  </div>
                  <div className="flex-1 min-h-[120px] relative p-4" style={{
                cursor: isZoomed ? 'grab' : 'crosshair',
                userSelect: 'none',
                WebkitUserSelect: 'none'
              }} ref={deltaChartContainerRef} onMouseDown={handleChartMouseDown} data-id="element-3310">
                    {isZoomed && deltaEntries.length > 0 && <button onClick={e => {
                  e.stopPropagation();
                  resetZoom();
                }} onMouseDown={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} className={`absolute top-2 right-2 z-20 flex items-center gap-1.5 text-[10px] font-semibold h-7 px-2 rounded-lg cursor-pointer outline-none border transition-all ${isLight ? 'bg-black/[0.03] border-black/[0.08] text-slate-600 hover:bg-black/[0.06] hover:text-slate-900' : 'bg-white/[0.04] border-white/[0.08] text-slate-300 hover:bg-white/[0.08] hover:text-white'}`} data-id="element-3311">
                        <ZoomOut size={10} data-id="element-3312" />
                        Reset zoom
                      </button>}
                    {deltaEntries.length === 0 ? <div className="flex items-center justify-center h-full" data-id="element-3313">
                        <span className="text-xs font-medium" style={{
                    color: isLight ? '#94a3b8' : '#475569'
                  }} data-id="element-3314">
                          Select at least 2 model runs to see delta comparison
                        </span>
                      </div> : <ResponsiveContainer width="100%" height="100%" debounce={100} data-id="element-3315">
                        <LineChart syncId="energy-market-sync" data={zoomedDeltaData} margin={{
                    top: 4,
                    right: 4,
                    bottom: 0,
                    left: 0
                  }} data-id="element-3316">
                          <defs data-id="element-3317">
                            {deltaEntries.map(entry => <filter key={`delta-glow-b-${entry.key}`} id={`delta-glow-b-${entry.key}`} x="-20%" y="-20%" width="140%" height="140%" data-id="element-3318">
                                <feGaussianBlur in="SourceGraphic" stdDeviation={isLight ? 2 : 3.5} result="blur" data-id="element-3319" />
                                <feColorMatrix in="blur" type="matrix" values={`1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 ${isLight ? 0.25 : 0.5} 0`} result="glow" data-id="element-3320" />
                                <feMerge data-id="element-3321">
                                  <feMergeNode in="glow" data-id="element-3322" />
                                  <feMergeNode in="SourceGraphic" data-id="element-3323" />
                                </feMerge>
                              </filter>)}
                          </defs>
                          {/* Weekend shading */}
                          {weekendRanges.map((range, i) => <ReferenceArea key={`weekend-delta-${i}`} x1={range.start} x2={range.end} fill={isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)'} fillOpacity={1} ifOverflow="extendDomain" data-id="element-3324" />)}
                          <CartesianGrid strokeDasharray="3 3" stroke={isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)'} vertical={false} data-id="element-3325" />
                          {/* Midnight markers */}
                          {midnightHours.map(m => <ReferenceLine key={`midnight-delta-${m.hour}`} x={m.hour} stroke={isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.15)'} strokeWidth={1} strokeDasharray="4 2" label={(props: any) => {
                      const {
                        viewBox
                      } = props;
                      const x = viewBox?.x ?? 0;
                      const y = viewBox?.y ?? 0;
                      return <g data-id="element-3327">
                                    <text x={x + 4} y={y + 12} fill={isLight ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.2)'} fontSize={9} fontWeight={700} data-id="element-3328">
                                      {m.dow}
                                    </text>
                                    <text x={x} y={y - 4} fill={isLight ? '#94a3b8' : '#475569'} fontSize={9} fontWeight={600} textAnchor="middle" data-id="element-3329">
                                      {m.label}
                                    </text>
                                  </g>;
                    }} data-id="element-3326" />)}
                          <XAxis dataKey="hour" stroke={isLight ? '#94a3b8' : '#475569'} fontSize={10} axisLine={false} tickLine={false} tickFormatter={formatHourTick} height={22} data-id="element-3330" />
                          <YAxis stroke={isLight ? '#94a3b8' : '#475569'} fontSize={10} axisLine={false} tickLine={false} tickFormatter={(v: number) => (v >= 0 ? '+' : '') + v.toFixed(1)} width={44} tickCount={6} data-id="element-3331" />
                          <ReferenceLine y={0} stroke={isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.1)'} strokeDasharray="4 3" data-id="element-3332" />
                          <Tooltip isAnimationActive={false} animationDuration={0} cursor={<CrosshairCursor stroke={isLight ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.35)'} strokeDasharray="4 3" data-id="element-3334" />} content={({
                      active,
                      payload,
                      label
                    }) => {
                      if (!active || !payload?.length) return null;
                      return <div className="rounded-xl px-3 py-2 border shadow-2xl" style={{
                        background: isLight ? 'rgba(255,255,255,0.58)' : 'rgba(15, 23, 42, 0.5)',
                        borderColor: isLight ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(20px) saturate(1.6)',
                        WebkitBackdropFilter: 'blur(20px) saturate(1.6)',
                        boxShadow: isLight ? '0 8px 32px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.6) inset' : '0 8px 32px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.07) inset'
                      }} data-id="element-3335">
                                  <div className="text-[11px] font-medium mb-1.5" style={{
                          color: isLight ? '#64748b' : '#94a3b8'
                        }} data-id="element-3336">
                                    +{Math.round(label as number)}h
                                  </div>
                                  <div className="space-y-1" data-id="element-3337">
                                    {payload.map((item: any) => {
                            const entryKey = String(item.dataKey).replace('delta-', '');
                            const entry = deltaEntries.find(e => e.key === entryKey);
                            return <div key={item.dataKey} className="flex items-center justify-between gap-4" data-id="element-3338">
                                          <div className="flex items-center gap-1.5" data-id="element-3339">
                                            <div className="w-2 h-[2px] rounded-full" style={{
                                  backgroundColor: item.color
                                }} data-id="element-3340" />
                                            <span className="text-[11px] font-medium" style={{
                                  color: isLight ? '#64748b' : '#cbd5e1'
                                }} data-id="element-3341">
                                              {entry ? `${entry.modelName} · ${entry.runLabel}` : item.name}
                                            </span>
                                          </div>
                                          <span className="text-[12px] font-bold tabular-nums" style={{
                                color: (item.value ?? 0) >= 0 ? '#10b981' : '#ef4444'
                              }} data-id="element-3342">
                                            {(item.value ?? 0) >= 0 ? '+' : ''}
                                            {typeof item.value === 'number' ? item.value.toFixed(2) : item.value}{' '}
                                            {varConfig.unit}
                                          </span>
                                        </div>;
                          })}
                                  </div>
                                </div>;
                    }} data-id="element-3333" />
                          {deltaEntries.map(entry => <Line key={`delta-${entry.key}`} type="monotone" dataKey={`delta-${entry.key}`} name={`${entry.modelName} · ${entry.runLabel}`} stroke={entry.color} strokeWidth={1.2} filter={`url(#delta-glow-b-${entry.key})`} dot={false} isAnimationActive={false} activeDot={(props: any) => {
                      const {
                        cx,
                        cy
                      } = props;
                      return <g data-id="element-3344">
                                    <circle cx={cx} cy={cy} r={6} fill={entry.color} fillOpacity={0.15} data-id="element-3345" />
                                    <line x1={cx - 5} y1={cy} x2={cx + 5} y2={cy} stroke={entry.color} strokeWidth={0.8} strokeOpacity={0.5} data-id="element-3346" />
                                    <line x1={cx} y1={cy - 5} x2={cx} y2={cy + 5} stroke={entry.color} strokeWidth={0.8} strokeOpacity={0.5} data-id="element-3347" />
                                    <path d={`M${cx},${cy - 3.5} L${cx + 3.5},${cy} L${cx},${cy + 3.5} L${cx - 3.5},${cy} Z`} fill={entry.color} stroke={isLight ? '#fff' : '#0f172a'} strokeWidth={1.5} data-id="element-3348" />
                                  </g>;
                    }} data-id="element-3343" />)}
                        </LineChart>
                      </ResponsiveContainer>}
                  </div>
                </div>}

              {bottomTab === 'table' && <div className="flex flex-col overflow-hidden flex-1 min-h-0" data-id="element-3349">
                  {/* Toolbar */}
                  <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{
                borderColor: isLight ? 'var(--border-default)' : 'rgba(255,255,255,0.06)',
                background: isLight ? 'var(--bg-surface)' : 'rgba(11, 17, 32, 0.80)'
              }} data-id="element-3350">
                    <div className="flex items-center gap-4" data-id="element-3351">
                      <h3 className="text-sm font-semibold" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-3352">
                        Hourly Delta Heatmap
                      </h3>
                      <span className="text-[10px] font-medium" style={{
                    color: isLight ? '#94a3b8' : '#64748b'
                  }} data-id="element-3353">
                        Δ {varConfig.unit} vs{' '}
                        {heatmapBaselineKey === null ? 'mean' : (() => {
                      const entry = chartEntries.find(e => e.key === heatmapBaselineKey);
                      return entry ? entry.modelName : 'mean';
                    })()}{' '}
                        · {chartEntries.length} run
                        {chartEntries.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-3354">
                      <div className="flex items-center gap-2 text-[9px]" style={{
                    color: isLight ? '#94a3b8' : '#64748b'
                  }} data-id="element-3355">
                        <span className="flex items-center gap-1" data-id="element-3356">
                          <div className="w-2 h-2 rounded-sm" style={{
                        backgroundColor: isLight ? 'rgba(96,165,250,0.3)' : 'rgba(59,130,246,0.4)'
                      }} data-id="element-3357" />{' '}
                          {heatmapBaselineKey === null ? 'Above mean' : 'Above baseline'}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-3358">
                          <div className="w-2 h-2 rounded-sm" style={{
                        backgroundColor: isLight ? 'rgba(244,114,182,0.28)' : 'rgba(236,72,153,0.4)'
                      }} data-id="element-3359" />{' '}
                          {heatmapBaselineKey === null ? 'Below mean' : 'Below baseline'}
                        </span>
                      </div>

                      {/* Baseline Selector */}
                      {chartEntries.length >= 1 && <div className="relative" ref={heatmapBaselineRef} data-id="element-3360">
                          <button onClick={() => setHeatmapBaselineOpen(!heatmapBaselineOpen)} className={`jua-toolbar-btn flex items-center gap-1.5 text-[10px] font-semibold h-6 px-2 rounded-md cursor-pointer outline-none border transition-all ${heatmapBaselineOpen ? 'bg-[#EC4899]/10 border-[#EC4899]/30 text-[#EC4899]' : isLight ? 'bg-black/[0.04] border-black/[0.08] text-slate-500 hover:bg-black/[0.08] hover:text-slate-700' : 'bg-white/[0.04] border-white/[0.08] text-slate-300 hover:bg-white/[0.08] hover:text-white'}`} data-id="element-3361">
                            vs{' '}
                            {heatmapBaselineKey === null ? 'Mean' : (() => {
                        const entry = chartEntries.find(e => e.key === heatmapBaselineKey);
                        return entry ? `${entry.modelName} ${entry.runLabel}` : 'Mean';
                      })()}
                            <ChevronDown size={9} className={`transition-transform ${heatmapBaselineOpen ? 'rotate-180' : ''}`} data-id="element-3362" />
                          </button>
                          {heatmapBaselineOpen && <div className="absolute right-0 top-full mt-1 rounded-lg border shadow-2xl overflow-hidden z-50 min-w-[180px]" style={{
                      background: isLight ? 'rgba(255,255,255,0.98)' : 'rgba(11, 17, 32, 0.97)',
                      borderColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.08)',
                      backdropFilter: 'blur(20px)'
                    }} data-id="element-3363">
                              <div className="px-2.5 py-1.5 border-b" style={{
                        borderColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'
                      }} data-id="element-3364">
                                <span className="text-[9px] font-semibold uppercase tracking-wider" style={{
                          color: isLight ? '#94a3b8' : '#475569'
                        }} data-id="element-3365">
                                  Baseline
                                </span>
                              </div>
                              <div className="py-1 max-h-[200px] overflow-y-auto" data-id="element-3366">
                                {/* Mean option */}
                                <button onClick={() => {
                          setHeatmapBaselineKey(null);
                          setHeatmapBaselineOpen(false);
                        }} className="w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors" style={{
                          backgroundColor: heatmapBaselineKey === null ? isLight ? 'rgba(236,72,153,0.08)' : 'rgba(236,72,153,0.12)' : 'transparent'
                        }} onMouseEnter={e => {
                          if (heatmapBaselineKey !== null) e.currentTarget.style.backgroundColor = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)';
                        }} onMouseLeave={e => {
                          if (heatmapBaselineKey !== null) e.currentTarget.style.backgroundColor = 'transparent';
                        }} data-id="element-3367">
                                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                            backgroundColor: isLight ? '#94a3b8' : '#64748b'
                          }} data-id="element-3368" />
                                  <span className="text-[11px] font-medium flex-1" style={{
                            color: heatmapBaselineKey === null ? '#EC4899' : isLight ? '#334155' : '#e2e8f0'
                          }} data-id="element-3369">
                                    Mean (all runs)
                                  </span>
                                  {heatmapBaselineKey === null && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899]" data-id="element-3370" />}
                                </button>
                                {/* Individual model runs */}
                                {chartEntries.map(entry => {
                          const isSelected = entry.key === heatmapBaselineKey;
                          return <button key={entry.key} onClick={() => {
                            setHeatmapBaselineKey(entry.key);
                            setHeatmapBaselineOpen(false);
                          }} className="w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors" style={{
                            backgroundColor: isSelected ? isLight ? 'rgba(236,72,153,0.08)' : 'rgba(236,72,153,0.12)' : 'transparent'
                          }} onMouseEnter={e => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)';
                          }} onMouseLeave={e => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                          }} data-id="element-3371">
                                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                              backgroundColor: entry.color
                            }} data-id="element-3372" />
                                      <span className="text-[11px] font-medium flex-1" style={{
                              color: isSelected ? '#EC4899' : isLight ? '#334155' : '#e2e8f0'
                            }} data-id="element-3373">
                                        {entry.modelName} · {entry.runLabel}
                                      </span>
                                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899]" data-id="element-3374" />}
                                    </button>;
                        })}
                              </div>
                            </div>}
                        </div>}
                    </div>
                  </div>

                  {/* Table */}
                  <div className="overflow-auto" data-id="element-3375">
                    {(() => {
                  if (chartEntries.length === 0) {
                    return <div className="flex items-center justify-center py-12" data-id="element-3376">
                            <span className="text-sm" style={{
                        color: 'var(--text-muted)'
                      }} data-id="element-3377">
                              Select model runs to see hourly deviations
                            </span>
                          </div>;
                  }
                  const hours = Array.from({
                    length: totalHours
                  }, (_, i) => i);
                  // Compute baseline values (mean or specific model run)
                  const baselineEntry = heatmapBaselineKey !== null ? chartEntries.find(e => e.key === heatmapBaselineKey) : null;
                  const hourlyBaseline = hours.map(h => {
                    if (baselineEntry) return baselineEntry.data[h];
                    const vals = chartEntries.map(e => e.data[h]);
                    return vals.reduce((a, b) => a + b, 0) / vals.length;
                  });
                  // Compute deltas: entry value minus baseline
                  const deltas = chartEntries.map(entry => hours.map(h => +(entry.data[h] - hourlyBaseline[h]).toFixed(varConfig.decimals)));
                  // Find global max absolute delta for color scaling
                  const allDeltas = deltas.flat();
                  const maxAbs = Math.max(0.01, ...allDeltas.map(Math.abs));
                  const getCellStyle = (val: number) => {
                    const abs = Math.abs(val);
                    const t = Math.min(abs / maxAbs, 1); // 0..1 normalized intensity
                    if (t < 0.08) return {
                      bg: 'transparent',
                      cls: isLight ? 'text-gray-400' : 'text-slate-500'
                    };
                    if (val > 0) {
                      // Above mean: blue
                      if (isLight) {
                        const alpha = (0.06 + t * 0.28).toFixed(2);
                        return {
                          bg: `rgba(96, 165, 250, ${alpha})`,
                          cls: t > 0.6 ? 'text-blue-700 font-bold' : t > 0.3 ? 'text-blue-600' : 'text-blue-500'
                        };
                      }
                      const alpha = (0.12 + t * 0.5).toFixed(2);
                      return {
                        bg: `rgba(59, 130, 246, ${alpha})`,
                        cls: t > 0.6 ? 'text-blue-200 font-bold' : t > 0.3 ? 'text-blue-300' : 'text-blue-400'
                      };
                    }
                    // Below mean: brand pink (matches app accent)
                    if (isLight) {
                      const alpha = (0.06 + t * 0.3).toFixed(2);
                      return {
                        bg: `rgba(244, 114, 182, ${alpha})`,
                        cls: t > 0.6 ? 'text-pink-700 font-bold' : t > 0.3 ? 'text-pink-600' : 'text-pink-500'
                      };
                    }
                    const alpha = (0.12 + t * 0.5).toFixed(2);
                    return {
                      bg: `rgba(236, 72, 153, ${alpha})`,
                      cls: t > 0.6 ? 'text-pink-200 font-bold' : t > 0.3 ? 'text-pink-300' : 'text-pink-400'
                    };
                  };
                  return <table className="w-full text-xs border-collapse" data-id="element-3378">
                          <thead className="sticky top-0 z-10" data-id="element-3379">
                            <tr data-id="element-3380">
                              <th className="text-left py-2.5 px-4 font-medium w-44 sticky left-0 z-20" style={{
                          color: isLight ? 'var(--text-muted)' : '#94a3b8',
                          backgroundColor: isLight ? 'var(--bg-surface)' : '#0B1120',
                          borderBottom: isLight ? '1px solid var(--border-default)' : '1px solid rgba(255,255,255,0.06)'
                        }} data-id="element-3381">
                                Model / Run
                              </th>
                              {hours.map(h => <th key={h} className="text-center py-2.5 px-1.5 font-medium min-w-[56px]" style={{
                          color: isLight ? 'var(--text-muted)' : '#94a3b8',
                          backgroundColor: isLight ? 'var(--bg-surface)' : '#0B1120',
                          borderBottom: isLight ? '1px solid var(--border-default)' : '1px solid rgba(255,255,255,0.06)'
                        }} data-id="element-3382">
                                  <div className="flex flex-col items-center leading-tight whitespace-nowrap" data-id="element-3383">
                                    {(() => {
                              const d = new Date();
                              d.setHours(d.getHours() + h);
                              return <>
                                          <span className="whitespace-nowrap" data-id="element-3384">
                                            {d.toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short'
                                  })}
                                          </span>
                                          <span className="whitespace-nowrap" data-id="element-3385">
                                            {String(d.getHours()).padStart(2, '0')}
                                            :00
                                          </span>
                                        </>;
                            })()}
                                  </div>
                                </th>)}
                            </tr>
                          </thead>
                          <tbody data-id="element-3386">
                            {chartEntries.map((entry, ei) => <tr key={entry.key} className={`group transition-colors ${isLight ? 'hover:bg-gray-50' : 'hover:bg-white/[0.02]'}`} data-id="element-3387">
                                <td className="py-1 px-3 font-medium sticky left-0 z-10 transition-colors" style={{
                          color: isLight ? 'var(--text-secondary)' : '#e2e8f0',
                          backgroundColor: isLight ? 'rgba(255, 255, 255, 0.85)' : 'rgba(11, 17, 32, 0.88)',
                          backdropFilter: 'blur(12px)',
                          WebkitBackdropFilter: 'blur(12px)',
                          borderBottom: isLight ? '1px solid var(--border-subtle)' : '1px solid rgba(255,255,255,0.04)'
                        }} data-id="element-3388">
                                  <div className="flex items-center gap-2" data-id="element-3389">
                                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                              backgroundColor: entry.color,
                              opacity: entry.ageOpacity
                            }} data-id="element-3390" />
                                    <span className="truncate whitespace-nowrap" data-id="element-3391">
                                      {entry.modelName}
                                    </span>
                                    <span className="text-[9px] font-mono whitespace-nowrap" style={{
                              color: isLight ? 'var(--text-muted)' : 'rgba(255,255,255,0.3)'
                            }} data-id="element-3392">
                                      {entry.runLabel}
                                    </span>
                                  </div>
                                </td>
                                {deltas[ei].map((val, hi) => {
                          const {
                            bg,
                            cls
                          } = getCellStyle(val);
                          return <td key={hi} className="py-0.5 px-1 text-center" style={{
                            borderBottom: isLight ? '1px solid var(--border-subtle)' : '1px solid rgba(255,255,255,0.04)'
                          }} data-id="element-3393">
                                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold tabular-nums ${cls}`} style={{
                              backgroundColor: bg
                            }} data-id="element-3394">
                                        {val >= 0 ? '+' : ''}
                                        {val.toFixed(varConfig.decimals > 1 ? 1 : varConfig.decimals)}
                                      </span>
                                    </td>;
                        })}
                              </tr>)}
                          </tbody>
                        </table>;
                })()}
                  </div>
                </div>}
            </Card>
          </div>
        </div>
      </div>
    </div>;
}